import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend, PieChart, Pie, Cell } from 'recharts';
import {
  LogIn, LogOut, Menu, X, Home, Users, Store, CreditCard,
  Clock, DollarSign, FileText, ChevronDown, Plus, PlusCircle,
  Edit, Trash2, Eye, Check, AlertCircle, TrendingUp, TrendingDown,
  Wallet, Receipt, Building2, Loader2, Search, Download,
  Save, RefreshCw, CheckCircle, XCircle, Upload, Image,
  ArrowRight, Coins, Send, PiggyBank, Car, MoreHorizontal, Settings,
  Banknote, ArrowUpRight, ArrowDownRight, ArrowDownLeft, Timer, Info, MessageSquare, Server, History, Percent, Calendar, ClipboardList,
  ShoppingBag, Wrench, ShieldCheck, Key, BarChart2
} from 'lucide-react';
import './App.css';
import DemoModeControl from './components/DemoModeControl';
import DepositManagement from './components/DepositManagement';
import OwnerProTools from './components/OwnerProTools';
import ElectricBorder from './components/ElectricBorder';
import Lightning from './components/Lightning';
import TanyaITModal from './components/TanyaITModal';
import TanyaITList from './components/TanyaITList';
import ITDashboard from './components/ITDashboard';
import SystemLogViewer from './components/SystemLogViewer';
import InjectSaldoManager from './InjectSaldoManager';
import RequestInjectSaldo from './RequestInjectSaldo';

// Use relative URLs for production (nginx proxy), fallback to localhost for dev
const API_URL = process.env.REACT_APP_BACKEND_URL !== undefined && process.env.REACT_APP_BACKEND_URL !== ''
  ? process.env.REACT_APP_BACKEND_URL
  : '';

// Auth Context
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

// API Helper
const api = {
  getHeaders: () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  },
  
  async request(endpoint, options = {}) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: options.headers || this.getHeaders()
    });
    
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('currentUser');
      window.location.reload();
    }
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Terjadi kesalahan');
    return data;
  },
  
  get: (endpoint) => api.request(endpoint),
  post: (endpoint, body) => api.request(endpoint, { method: 'POST', body: JSON.stringify(body) }),
  put: (endpoint, body) => api.request(endpoint, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: (endpoint) => api.request(endpoint, { method: 'DELETE' }),
  
  async uploadFile(endpoint, file) {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    
    const data = await response.json();
    if (!response.ok) throw new Error(data.detail || 'Upload gagal');
    return data;
  }
};

// Format currency
const formatRupiah = (num) => {
  if (!num && num !== 0) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num);
};

// Parse date - handle extended JSON {"$date": "..."}
const parseDate = (d) => {
  if (!d) return null;
  if (typeof d === 'object' && d.$date) return new Date(d.$date);
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

// Format date
const formatDate = (date) => {
  if (!date) return '-';
  const dt = parseDate(date);
  if (!dt) return '-';
  return dt.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (date) => {
  if (!date) return '-';
  const dt = parseDate(date);
  if (!dt) return '-';
  return dt.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ==================== COMPONENTS ====================

const Alert = ({ type = 'info', message, onClose }) => {
  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };
  
  return (
    <div className={`p-4 border rounded-lg flex items-center justify-between ${colors[type]} fade-in`}>
      <span>{message}</span>
      {onClose && <button onClick={onClose} className="ml-4 hover:opacity-70"><X size={18} /></button>}
    </div>
  );
};

const LoadingSpinner = ({ size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-8 h-8', lg: 'w-12 h-12' };
  return (
    <div className="flex justify-center items-center p-4">
      <Loader2 className={`${sizes[size]} text-primary-600 spinner`} />
    </div>
  );
};

const Button = ({ children, variant = 'primary', size = 'md', loading, disabled, className = '', ...props }) => {
  const variants = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
    success: 'bg-green-600 hover:bg-green-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    warning: 'bg-yellow-500 hover:bg-yellow-600 text-white',
    outline: 'border-2 border-primary-600 text-primary-600 hover:bg-primary-50'
  };
  
  const sizes = { sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2', lg: 'px-6 py-3 text-lg' };
  
  return (
    <button
      className={`${variants[variant]} ${sizes[size]} rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 spinner" />}
      {children}
    </button>
  );
};

const Card = ({ children, className = '', hover = false }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 ${hover ? 'card-hover' : ''} ${className}`}>
    {children}
  </div>
);

const Input = ({ label, error, className = '', ...props }) => (
  <div className={className}>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <input
      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${error ? 'border-red-500' : 'border-gray-300'}`}
      {...props}
    />
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

const Select = ({ label, error, options = [], className = '', ...props }) => (
  <div className={className}>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
    <select className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${error ? 'border-red-500' : 'border-gray-300'}`} {...props}>
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        <div className={`relative bg-white rounded-xl shadow-xl ${sizes[size]} w-full mx-auto fade-in`}>
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
          <div className="p-4 max-h-[80vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
  const colors = { blue: 'bg-blue-500', green: 'bg-green-500', yellow: 'bg-yellow-500', purple: 'bg-purple-500', red: 'bg-red-500' };
  
  return (
    <Card className="p-5" hover>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colors[color]} text-white`}><Icon size={24} /></div>
      </div>
    </Card>
  );
};

const StatusBadge = ({ status, showRole = true }) => {
  // Color scheme based on status and role
  const colors = {
    // Pending
    PENDING: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    
    // Kasir verification
    VERIFIED_KASIR: 'bg-blue-100 text-blue-800 border border-blue-300',
    REJECTED_KASIR: 'bg-orange-100 text-orange-800 border border-orange-300',
    
    // Manager verification  
    VERIFIED_MANAGER: 'bg-purple-100 text-purple-800 border border-purple-300',
    REJECTED_MANAGER: 'bg-pink-100 text-pink-800 border border-pink-300',
    
    // Owner verification
    VERIFIED_OWNER: 'bg-green-100 text-green-800 border border-green-300',
    REJECTED_OWNER: 'bg-red-100 text-red-800 border border-red-300',
    
    // Legacy/simple status
    VERIFIED: 'bg-green-100 text-green-800 border border-green-300',
    REJECTED: 'bg-red-100 text-red-800 border border-red-300',
    AKTIF: 'bg-green-100 text-green-800',
    NONAKTIF: 'bg-gray-100 text-gray-800'
  };
  
  // Display labels
  const labels = {
    PENDING: '⏳ Pending',
    VERIFIED_KASIR: '✓ Kasir OK',
    REJECTED_KASIR: '✗ Ditolak Kasir',
    VERIFIED_MANAGER: '✓✓ Manager OK',
    REJECTED_MANAGER: '✗ Ditolak Manager',
    VERIFIED_OWNER: '✓✓✓ Owner OK',
    REJECTED_OWNER: '✗ Ditolak Owner',
    VERIFIED: '✓ Verified',
    REJECTED: '✗ Rejected',
    AKTIF: 'Aktif',
    NONAKTIF: 'Nonaktif'
  };
  
  const displayLabel = showRole ? (labels[status] || status) : status;
  
  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {displayLabel}
    </span>
  );
};

// Verification Modal with Note
const VerifyModal = ({ isOpen, onClose, onVerify, title, itemInfo }) => {
  const [action, setAction] = useState('APPROVED');
  const [catatan, setCatatan] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onVerify(action, catatan);
      setCatatan('');
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        {itemInfo && (
          <div className="p-3 bg-gray-50 rounded-lg text-sm">
            {itemInfo}
          </div>
        )}
        
        <div className="flex gap-4">
          <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${action === 'APPROVED' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
            <input type="radio" name="action" value="APPROVED" checked={action === 'APPROVED'} onChange={(e) => setAction(e.target.value)} className="sr-only" />
            <div className="text-center">
              <CheckCircle className={`mx-auto mb-2 ${action === 'APPROVED' ? 'text-green-600' : 'text-gray-400'}`} size={32} />
              <p className="font-semibold text-green-700">APPROVE</p>
              <p className="text-xs text-gray-500">Setujui data ini</p>
            </div>
          </label>
          
          <label className={`flex-1 p-4 border-2 rounded-lg cursor-pointer transition-all ${action === 'REJECTED' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
            <input type="radio" name="action" value="REJECTED" checked={action === 'REJECTED'} onChange={(e) => setAction(e.target.value)} className="sr-only" />
            <div className="text-center">
              <XCircle className={`mx-auto mb-2 ${action === 'REJECTED' ? 'text-red-600' : 'text-gray-400'}`} size={32} />
              <p className="font-semibold text-red-700">REJECT</p>
              <p className="text-xs text-gray-500">Tolak data ini</p>
            </div>
          </label>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Catatan {action === 'REJECTED' ? <span className="text-red-500">*</span> : '(Opsional)'}
          </label>
          <textarea
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            rows={3}
            value={catatan}
            onChange={(e) => setCatatan(e.target.value)}
            placeholder={action === 'APPROVED' ? 'Catatan persetujuan (opsional)...' : 'Alasan penolakan harus diisi...'}
            required={action === 'REJECTED'}
          />
        </div>
        
        <div className="flex gap-3 pt-2">
          <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>Batal</Button>
          <Button 
            className={`flex-1 ${action === 'APPROVED' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
            onClick={handleSubmit}
            loading={submitting}
            disabled={action === 'REJECTED' && !catatan.trim()}
          >
            {action === 'APPROVED' ? <CheckCircle size={18} /> : <XCircle size={18} />}
            {action === 'APPROVED' ? 'Approve' : 'Reject'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const FileUpload = ({ onUpload, uploading }) => {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSave = () => {
    if (selectedFile) {
      onUpload(selectedFile);
      setSelectedFile(null);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-center gap-2">
      <input 
        type="file" 
        ref={inputRef} 
        className="hidden" 
        accept="image/*,.pdf" 
        onChange={handleFileChange} 
      />
      {!selectedFile ? (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => inputRef.current?.click()} 
          loading={uploading}
          className="flex items-center gap-1"
        >
          <Upload size={16} /> Pilih Bukti
        </Button>
      ) : (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-lg text-xs shadow-sm">
          <span className="truncate max-w-[150px] font-semibold text-gray-700">📄 {selectedFile.name}</span>
          <button 
            type="button"
            onClick={() => setSelectedFile(null)}
            className="text-red-500 hover:text-red-700 font-medium px-2 py-1 hover:bg-red-50 rounded"
          >
            Batal
          </button>
          <Button 
            variant="success" 
            size="sm" 
            onClick={handleSave} 
            loading={uploading}
            className="flex items-center gap-1 px-3 py-1"
          >
            <Check size={14} /> Simpan
          </Button>
        </div>
      )}
    </div>
  );
};

const BuktiGallery = ({ urls = [] }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  if (urls.length === 0) return <p className="text-sm text-gray-500">Belum ada bukti</p>;
  
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {urls.map((url, idx) => (
          <div key={idx} className="relative group">
            <img src={`${API_URL}${url}`} alt={`Bukti ${idx + 1}`} className="w-16 h-16 object-cover rounded-lg cursor-pointer border" onClick={() => setSelectedImage(url)} />
            <a href={`${API_URL}${url}`} download className="absolute -top-1 -right-1 bg-primary-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <Download size={12} />
            </a>
          </div>
        ))}
      </div>
      
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <img src={`${API_URL}${selectedImage}`} alt="Preview" className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 text-white"><X size={32} /></button>
        </div>
      )}
    </>
  );
};

// ==================== LOGIN PAGE ====================

const LoginPage = ({ onLogin }) => {
  const [currentUsername, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [seeding, setSeeding] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const data = await api.post('/api/auth/login', { username: currentUsername, password });
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('currentUser', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const result = await fetch(`${API_URL}/api/seed`, { method: 'POST' });
      const data = await result.json();
      if (data.status === 'success') {
        alert('Data demo berhasil dibuat!\n\nAkun tersedia:\n- owner / owner123 (Owner)\n- manager / manager123 (Manager)\n- kasir / kasir123 (Kasir)\n- penjaga1 / penjaga123 (Loket Ketapang)\n- penjaga2 / penjaga123 (Loket Merdeka)\n- penjaga3 / penjaga123 (Loket Sudirman)');
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert('Gagal membuat data demo: ' + err.message);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Lightning effect disabled for performance */}
      {/* <Lightning
        hue={190}
        xOffset={0}
        speed={1.2}
        intensity={0.9}
        size={0.7}
      /> */}
      {/* ElectricBorder effect disabled for performance */}
      {/* <ElectricBorder
        color="#7df9ff"
        speed={1.5}
        chaos={0.5}
        borderRadius={24}
        className="w-full max-w-md"
      > */}
        <Card className="w-full max-w-md p-10">
        <div className="text-center mb-10">
          {/* Logo Container dengan futuristic multi-layer effects */}
          <div className="relative inline-flex items-center justify-center mb-6 group cursor-pointer">
            {/* Layer 1 - Massive outer glow - menyatu sempurna dengan background */}
            <div className="absolute w-72 h-72 bg-gradient-to-br from-primary-300 via-primary-500 via-primary-600 to-primary-700 rounded-full blur-3xl opacity-25 animate-pulse group-hover:opacity-40 group-hover:scale-110 transition-all duration-500 ease-out"></div>

            {/* Layer 2 - Secondary glow ring */}
            <div className="absolute w-64 h-64 bg-gradient-to-tr from-primary-400 via-primary-500 to-primary-700 rounded-full blur-2xl opacity-30 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500 ease-out"></div>

            {/* Layer 3 - Tertiary glow */}
            <div className="absolute w-56 h-56 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-800 rounded-full blur-xl opacity-40 group-hover:opacity-60 transition-all duration-500"></div>

            {/* Layer 4 - Quaternary inner glow */}
            <div className="absolute w-48 h-48 bg-gradient-to-tr from-white/40 via-primary-100 to-primary-300 rounded-full blur-lg opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>

            {/* Layer 5 - Glass-morphism futuristic container with hover effects */}
            <div className="relative w-52 h-52 bg-gradient-to-br from-white/95 via-white/90 to-white/85 backdrop-blur-md rounded-3xl flex items-center justify-center shadow-[0_0_60px_rgba(37,99,235,0.5),0_0_100px_rgba(37,99,235,0.3),0_25px_50px_-12px_rgba(0,0,0,0.25)] border-2 border-white/60 relative overflow-hidden group-hover:scale-110 group-hover:shadow-[0_0_80px_rgba(37,99,235,0.7),0_0_120px_rgba(37,99,235,0.5),0_30px_60px_-12px_rgba(0,0,0,0.35)] transition-all duration-500 ease-out group-hover:border-primary-300/80">

              {/* Animated shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

              {/* Rotating gradient border on hover */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-primary-400/20 via-transparent to-primary-600/20 animate-spin-slow"></div>
              </div>

              {/* Inner glow accent */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary-500/10 via-transparent to-primary-700/20 group-hover:from-primary-500/20 group-hover:to-primary-700/30 transition-all duration-500"></div>

              {/* Logo image with hover animations */}
              <img
                src="/logo.png"
                alt="SIP GELIS V5.0 Logo"
                className="relative w-48 h-48 object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.3)] filter brightness-110 contrast-105 group-hover:scale-110 group-hover:rotate-6 group-hover:drop-shadow-[0_15px_40px_rgba(37,99,235,0.4)] group-hover:brightness-125 group-hover:contrast-110 transition-all duration-500 ease-out"
              />

              {/* Top highlight line */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent group-hover:h-0.5 transition-all duration-300"></div>

              {/* Sparkle effects on hover */}
              <div className="absolute top-2 right-2 w-2 h-2 bg-white rounded-full opacity-0 group-hover:opacity-100 group-hover:scale-150 transition-all duration-500 delay-100"></div>
              <div className="absolute bottom-3 left-3 w-1.5 h-1.5 bg-primary-300 rounded-full opacity-0 group-hover:opacity-100 group-hover:scale-125 transition-all duration-500 delay-200"></div>
              <div className="absolute top-1/2 right-4 w-1 h-1 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-300"></div>
            </div>
          </div>

          {/* Judul dengan tipografi futuristic */}
          <h1 className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary-700 via-primary-600 to-primary-800 mb-2 tracking-tight drop-shadow-lg">
            SIP GELIS V5.0
          </h1>

          {/* Deskripsi dengan styling yang lebih halus */}
          <p className="text-sm text-gray-700 font-semibold tracking-wide uppercase mb-1">
            Sistem Informasi dan Pelaporan Gerai Listrik
          </p>

          {/* Version */}
          <p className="text-xs text-gray-500 font-medium">
            Version 5.0
          </p>

          {/* Futuristic garis dekoratif - mepet dengan logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-primary-500 rounded-full"></div>
            <div className="w-16 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-500 rounded-full shadow-[0_0_20px_rgba(37,99,235,0.6)]"></div>
            <div className="w-8 h-0.5 bg-gradient-to-l from-transparent to-primary-500 rounded-full"></div>
          </div>
        </div>
        
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="login-form">
          <Input label="Username" value={currentUsername} onChange={(e) => setUsername(e.target.value)} placeholder="Masukkan username" required data-testid="login-username" />
          <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Masukkan password" required data-testid="login-password" />
          <Button type="submit" className="w-full" loading={loading} data-testid="login-submit"><LogIn size={18} /> Masuk</Button>
        </form>
        
        <div className="mt-6 pt-6 border-t">
          <p className="text-sm text-gray-500 text-center mb-3">Pertama kali? Buat data demo:</p>
          <Button variant="outline" className="w-full" onClick={handleSeed} loading={seeding} data-testid="seed-data-btn"><RefreshCw size={18} /> Buat Data Demo</Button>
        </div>
      </Card>
      {/* </ElectricBorder> */}
    </div>
  );
};

// ==================== SIDEBAR ====================

const Sidebar = ({ currentPage, setCurrentPage, currentUser, onLogout, isMobileOpen, setIsMobileOpen, showTanyaITModal, setShowTanyaITModal }) => {
  const menuItems = {
    OWNER: [
      { id: 'dashboard', label: 'Dashboard', icon: Home },
      { id: 'dashboard-loket-owner', label: 'Dashboard Penjaga Loket', icon: BarChart2 },
      { id: 'dashboard-kasir-owner', label: 'Dashboard Kasir & Keuangan', icon: BarChart2 },
      { id: 'riwayat-loket', label: 'Riwayat Laporan Penjaga Loket', icon: Clock },
      { id: 'riwayat-laporan-kasir', label: 'Riwayat Laporan Kasir', icon: History },
      { id: 'verifikasi', label: 'Verifikasi', icon: CheckCircle },
      { id: 'tarik-data', label: 'Tarik Data', icon: Download },
      { id: 'laporan-keuangan', label: 'Laporan Keuangan', icon: FileText },
      { id: 'akuntansi', label: 'Akuntansi', icon: Coins },
      { id: 'inject-saldo', label: 'Inject Saldo', icon: PlusCircle },
      { id: 'penjaga-loket', label: 'Pengaturan Shift', icon: Timer },
      { id: 'technical-jobs', label: 'Pekerjaan Teknis', icon: Wrench },
      { id: 'program-csr', label: 'Program CSR & Loyalty', icon: TrendingUp },
      { id: 'loket', label: 'Manajemen Loket', icon: Store },
      { id: 'provider', label: 'Provider PPOB', icon: Receipt },
      { id: 'users', label: 'Manajemen User', icon: Users },
      { id: 'currentUser-credentials', label: 'Kelola User & Password', icon: Key },
      { id: 'pengaturan', label: 'Pengaturan', icon: Settings },
      { id: 'tanya-it', label: 'Tanya IT', icon: MessageSquare },
    ],
    MANAGER: [
      { id: 'dashboard', label: 'Dashboard', icon: Home },
      { id: 'dashboard-loket-owner', label: 'Dashboard Penjaga Loket', icon: BarChart2 },
      { id: 'dashboard-kasir-owner', label: 'Dashboard Kasir & Keuangan', icon: BarChart2 },
      { id: 'riwayat-loket', label: 'Riwayat Laporan Penjaga Loket', icon: Clock },
      { id: 'riwayat-laporan-kasir', label: 'Riwayat Laporan Kasir', icon: History },
      { id: 'verifikasi', label: 'Verifikasi', icon: CheckCircle },
      { id: 'tarik-data', label: 'Tarik Data', icon: Download },
      { id: 'akuntansi', label: 'Akuntansi', icon: Coins },
      { id: 'inject-saldo', label: 'Inject Saldo', icon: PlusCircle },
      { id: 'penjaga-loket', label: 'Pengaturan Shift', icon: Timer },
      { id: 'technical-jobs', label: 'Pekerjaan Teknis', icon: Wrench },
      { id: 'program-csr', label: 'Program CSR & Loyalty', icon: TrendingUp },
      { id: 'provider', label: 'Provider PPOB', icon: Receipt },
      { id: 'loket', label: 'Manajemen Loket', icon: Store },
      { id: 'users', label: 'Manajemen User', icon: Users },
      { id: 'currentUser-credentials', label: 'Kelola User & Password', icon: Key },
      { id: 'tanya-it', label: 'Tanya IT', icon: MessageSquare },
    ],
    KASIR: [
      { id: 'dashboard-kasir', label: 'Dashboard Kasir', icon: Home },
      { id: 'laporan-kasir', label: 'Laporan Kasir', icon: Receipt },
      { id: 'riwayat-laporan-kasir', label: 'Riwayat Laporan', icon: History },
      { id: 'riwayat-loket', label: 'Riwayat Laporan Shift Penjaga Loket', icon: Clock },
      // { id: 'pengeluaran', label: 'Pengeluaran', icon: Wallet }, // Dihapus: pengeluaran sudah termasuk di laporan kasir
      { id: 'request-inject-saldo', label: 'Request Inject Saldo', icon: PlusCircle },
      { id: 'tarik-data-kasir', label: 'Tarik Data', icon: Download },
      { id: 'tanya-it', label: 'Tanya IT', icon: MessageSquare },
    ],
    SUPERKASIR: [
      { id: 'dashboard-kasir', label: 'Dashboard Superkasir', icon: Home },
      { id: 'riwayat-loket', label: 'Riwayat Laporan Shift', icon: Clock },
      { id: 'laporan-kasir', label: 'Laporan Kasir', icon: Receipt },
      { id: 'riwayat-laporan-kasir', label: 'Riwayat Laporan Kasir', icon: History },
      { id: 'request-inject-saldo', label: 'Request Inject Saldo', icon: PlusCircle },
      // { id: 'pengeluaran', label: 'Pengeluaran', icon: Wallet }, // Dihapus: pengeluaran sudah termasuk di laporan kasir
      { id: 'tanya-it', label: 'Tanya IT', icon: MessageSquare },
    ],
    PENJAGA_LOKET: [
      { id: 'dashboard-loket', label: 'Dashboard Loket', icon: Home },
      { id: 'laporan-shift', label: 'Buat Laporan Shift', icon: FileText },
      { id: 'request-inject-saldo', label: 'Request Inject Saldo', icon: PlusCircle },
      { id: 'tarik-data-loket', label: 'Tarik Data', icon: Download },
      { id: 'riwayat-loket', label: 'Riwayat Laporan', icon: Clock },
      { id: 'tanya-it', label: 'Tanya IT', icon: MessageSquare },
    ],
    IT: [
      { id: 'dashboard-it', label: 'Dashboard IT', icon: Settings },
      { id: 'verifikasi', label: 'Verifikasi', icon: CheckCircle },
      { id: 'tarik-data', label: 'Tarik Data', icon: Download },
      { id: 'laporan-keuangan', label: 'Laporan Keuangan', icon: FileText },
      { id: 'deposit-bank', label: 'Deposit Bank', icon: Banknote },
      { id: 'loket', label: 'Manajemen Loket', icon: Store },
      { id: 'provider', label: 'Provider PPOB', icon: Receipt },
      { id: 'users', label: 'Manajemen User', icon: Users },
      { id: 'system-logs', label: 'System Logs', icon: Server },
      { id: 'tanya-it', label: 'Tanya IT', icon: MessageSquare },
    ],
    TECHNICIAN: [
      { id: 'technical-jobs', label: 'Pekerjaan Teknis', icon: Wrench },
      { id: 'tanya-it', label: 'Tanya IT', icon: MessageSquare },
    ]
  };

  const filteredMenu = menuItems[currentUser?.role] || [];

  return (
    <>
      {isMobileOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsMobileOpen(false)} />}
      
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b">
            <div className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="SIP GELIS V5.0 Logo"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="font-bold text-gray-900">SIP GELIS V5.0</h1>
                <p className="text-xs text-gray-500">Sistem Informasi dan Pelaporan Gerai Listrik</p>
              </div>
            </div>
          </div>
          
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredMenu.map(item => (
              <button
                key={item.id}
                onClick={() => { setCurrentPage(item.id); setIsMobileOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${currentPage === item.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid={`menu-${item.id}`}
              >
                <item.icon size={20} />
                {item.label}
              </button>
            ))}
          </nav>
          
          <div className="p-4 border-t">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <Users size={20} className="text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{currentUser?.nama_lengkap}</p>
                <p className="text-xs text-gray-500">{currentUser?.role} {currentUser?.loket && `- ${currentUser.loket.nama}`}</p>
              </div>
            </div>
            {/* Tanya IT button - Hanya untuk non-IT role */}
            {currentUser?.role !== 'IT' && (
              <Button
                variant="outline"
                size="sm"
                className="w-full mb-2"
                onClick={() => setShowTanyaITModal(true)}
              >
                <MessageSquare size={16} /> Tanya IT
              </Button>
            )}
            <Button variant="secondary" size="sm" className="w-full" onClick={onLogout} data-testid="logout-btn"><LogOut size={16} /> Keluar</Button>
          </div>
        </div>
      </aside>
    </>
  );
};

// ==================== DASHBOARD LOKET (PENJAGA) ====================

const DashboardLoket = ({ currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loketPeriod, setLoketPeriod] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [allReports, setAllReports] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage] = useState(10);
  const [feeAdminPage, setFeeAdminPage] = useState(1);
  const [feeAdminPerPage] = useState(10);
  const [riwayatPage, setRiwayatPage] = useState(1);
  const [riwayatPerPage] = useState(10);

  useEffect(() => { loadData(); }, []);
  useEffect(() => { setHistoryPage(1); }, [loketPeriod, customDateRange.start, customDateRange.end]);
  useEffect(() => { setFeeAdminPage(1); }, [loketPeriod, customDateRange.start, customDateRange.end]);
  useEffect(() => { setRiwayatPage(1); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch directly from laporan-shift API to get all reports
      const reports = await api.get('/api/laporan-shift?limit=100');
      setAllReports(reports || []);

      // Also get loket info
      const lokets = await api.get('/api/lokets');
      const currentUserLoket = lokets?.find(l => l.id === currentUser.loket_id) || null;

      setData({
        loket: currentUserLoket,
        reports: reports || []
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  // Calculate filtered data based on selected period
  const getDateRange = () => {
    const today = new Date();
    const start = new Date(today);

    switch (loketPeriod) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case '7days':
        start.setDate(today.getDate() - 6);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case '30days':
        start.setDate(today.getDate() - 29);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case 'month':
        start.setDate(1);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case 'all':
        return { start: '2020-01-01', end: today.toISOString().split('T')[0] };
      case 'custom':
        return { start: customDateRange.start, end: customDateRange.end };
      default:
        start.setDate(today.getDate() - 6);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
    }
  };

  const calculateFilteredData = () => {
    if (!data) return null;

    const { start, end } = getDateRange();

    // Use allReports directly from API
    let filteredHistory = [];

    // Group reports by date
    const reportsByDate = {};
    allReports.forEach(lap => {
      const tgl = lap.tanggal;
      if (!reportsByDate[tgl]) {
        reportsByDate[tgl] = { tanggal: tgl, jumlah_laporan: 0, total_omzet: 0, total_admin_fee: 0, total_transaksi: 0, reports: [] };
      }
      reportsByDate[tgl].jumlah_laporan += 1;
      reportsByDate[tgl].total_omzet += lap.total_omzet || 0;
      reportsByDate[tgl].total_admin_fee += lap.total_admin_fee || 0;
      reportsByDate[tgl].total_transaksi += lap.total_transaksi || 0;
      reportsByDate[tgl].reports.push(lap);
    });

    // Filter by date range
    filteredHistory = Object.values(reportsByDate)
      .filter(day => day.tanggal >= start && day.tanggal <= end)
      .sort((a, b) => b.tanggal.localeCompare(a.tanggal)); // Sort descending by date

    const totalOmzet = filteredHistory.reduce((sum, day) => sum + (day.total_omzet || 0), 0);
    const totalAdminFee = filteredHistory.reduce((sum, day) => sum + (day.total_admin_fee || 0), 0);
    const totalTransaksi = filteredHistory.reduce((sum, day) => sum + (day.total_transaksi || 0), 0);
    const totalLaporan = filteredHistory.reduce((sum, day) => sum + (day.jumlah_laporan || 0), 0);

    const daysCount = filteredHistory.length || 1;
    const avgOmzet = totalOmzet / daysCount;
    const avgAdminFee = totalAdminFee / daysCount;

    // Aggregate fee admin per month for cumulative chart
    const monthlyAgg = {};
    filteredHistory.forEach(day => {
      const monthKey = day.tanggal.substring(0, 7); // YYYY-MM
      if (!monthlyAgg[monthKey]) {
        monthlyAgg[monthKey] = { bulan: monthKey, fee_admin: 0, omzet: 0, transaksi: 0, hari_aktif: 0 };
      }
      monthlyAgg[monthKey].fee_admin += day.total_admin_fee || 0;
      monthlyAgg[monthKey].omzet += day.total_omzet || 0;
      monthlyAgg[monthKey].transaksi += day.total_transaksi || 0;
      monthlyAgg[monthKey].hari_aktif += 1;
    });
    const monthlyHistory = Object.values(monthlyAgg).sort((a, b) => a.bulan.localeCompare(b.bulan));
    let runningTotal = 0;
    monthlyHistory.forEach(m => {
      runningTotal += m.fee_admin;
      m.kumulatif = runningTotal;
    });

    // Aggregate transactions by day of week
    const dayNames = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];
    const dayAgg = {};
    dayNames.forEach(d => { dayAgg[d] = { hari: d, transaksi: 0, omzet: 0, fee: 0, count: 0 }; });
    filteredHistory.forEach(day => {
      const dt = new Date(day.tanggal);
      const weekday = dt.getDay(); // 0=Minggu, 1=Senin, ...
      const dayName = dayNames[weekday];
      dayAgg[dayName].transaksi += day.total_transaksi || 0;
      dayAgg[dayName].omzet += day.total_omzet || 0;
      dayAgg[dayName].fee += day.total_admin_fee || 0;
      dayAgg[dayName].count += 1;
    });
    // Sort by transaction count descending to show busiest days first
    const dayOfWeekHistory = dayNames.map(d => dayAgg[d]).sort((a, b) => b.transaksi - a.transaksi);

    // Get latest report
    const latestReport = allReports[0] || null;

    return {
      filteredHistory,
      totalOmzet,
      totalAdminFee,
      totalTransaksi,
      totalLaporan,
      avgOmzet,
      avgAdminFee,
      monthlyHistory,
      dayOfWeekHistory,
      latestReport,
      periodLabel: loketPeriod === 'today' ? 'Hari Ini' :
                   loketPeriod === '7days' ? '7 Hari Terakhir' :
                   loketPeriod === '30days' ? '30 Hari Terakhir' :
                   loketPeriod === 'month' ? 'Bulan Ini' :
                   loketPeriod === 'all' ? 'Semua Data' :
                   loketPeriod === 'custom' ? `${formatDate(customDateRange.start)} - ${formatDate(customDateRange.end)}` : '7 Hari Terakhir',
      dateRange: { start, end }
    };
  };

  const filteredData = calculateFilteredData();
  const t = filteredData?.latestReport || {};

  return (
    <div className="space-y-6">
      {/* Header dengan info loket */}
      <div className="flex items-center justify-between">
        <div>
          {/* Dynamic title based on role */}
          {(currentUser?.role === 'OWNER' || currentUser?.role === 'MANAGER' || currentUser?.role === 'SUPERKASIR') ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Penjaga Loket</h1>
              <p className="text-sm text-indigo-600 font-medium mt-1">
                📊 Menampilkan data dari seluruh loket
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard Loket</h1>
              <div className="flex items-center gap-2 text-gray-500 mt-1">
                <span>{data?.loket?.nama_loket || 'Loket tidak ditemukan'}</span>
                {data?.loket?.kode_loket && (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-sm">Kode: {data.loket.kode_loket}</span>
                  </>
                )}
              </div>
              {data?.loket?.alamat && (
                <p className="text-sm text-gray-400 mt-0.5">{data.loket.alamat}</p>
              )}
            </>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={loadData}><RefreshCw size={16} /> Refresh</Button>
      </div>

      {/* Period Selector */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Calendar className="text-indigo-600" size={24} />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Ringkasan Loket</h2>
              <p className="text-sm text-gray-500">
                Periode: <span className="font-medium text-indigo-600">{filteredData?.periodLabel}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              value={loketPeriod}
              onChange={(e) => setLoketPeriod(e.target.value)}
            >
              <option value="today">Hari Ini</option>
              <option value="7days">7 Hari Terakhir</option>
              <option value="30days">30 Hari Terakhir</option>
              <option value="month">Bulan Ini</option>
              <option value="all">Semua Data</option>
              <option value="custom">Pilih Tanggal</option>
            </select>

            {loketPeriod === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                />
                <span className="text-gray-500">-</span>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Summary Cards - Period Based */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-purple-100 rounded-lg"><DollarSign className="text-purple-600" size={20} /></div>
            <p className="text-sm text-purple-700 font-medium">Total Omzet</p>
          </div>
          <p className="text-2xl font-bold text-purple-700">{formatRupiah(filteredData?.totalOmzet || 0)}</p>
          <p className="text-xs text-purple-600 mt-1">Rata-rata: {formatRupiah(filteredData?.avgOmzet || 0)}/hari</p>
        </div>

        <div className="p-5 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-green-100 rounded-lg"><Percent className="text-green-600" size={20} /></div>
            <p className="text-sm text-green-700 font-medium">Total Fee Admin</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{formatRupiah(filteredData?.totalAdminFee || 0)}</p>
          <p className="text-xs text-green-600 mt-1">Rata-rata: {formatRupiah(filteredData?.avgAdminFee || 0)}/hari</p>
        </div>

        <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-blue-100 rounded-lg"><CreditCard className="text-blue-600" size={20} /></div>
            <p className="text-sm text-blue-700 font-medium">Total Transaksi</p>
          </div>
          <p className="text-2xl font-bold text-blue-700">{filteredData?.totalTransaksi || 0}</p>
          <p className="text-xs text-blue-600 mt-1">{filteredData?.totalLaporan || 0} laporan</p>
        </div>

        <div className="p-5 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl border border-yellow-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-yellow-100 rounded-lg"><FileText className="text-yellow-600" size={20} /></div>
            <p className="text-sm text-yellow-700 font-medium">Jumlah Laporan</p>
          </div>
          <p className="text-2xl font-bold text-yellow-700">{filteredData?.totalLaporan || 0}</p>
          <p className="text-xs text-yellow-600 mt-1">{filteredData?.periodLabel}</p>
        </div>
      </div>

      {/* Trend Chart - Analytics */}
      {filteredData?.filteredHistory?.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg"><TrendingUp className="text-indigo-600" size={24} /></div>
            <h3 className="font-semibold text-gray-900">Tren {filteredData?.periodLabel}</h3>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tren Jumlah Transaksi per Hari */}
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">Tren Jumlah Transaksi per Hari</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[...filteredData.filteredHistory].reverse()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="tanggal" tickFormatter={(val) => val.substring(5)} fontSize={12} />
                    <YAxis tickFormatter={(val) => `${val}`} fontSize={12} />
                    <Tooltip formatter={(val) => `${val} trx`} />
                    <Line type="monotone" dataKey="total_transaksi" stroke="#8b5cf6" strokeWidth={2} dot={{fill: '#8b5cf6'}} name="Transaksi" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            {/* Tren Fee Admin per Hari */}
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-2">Tren Fee Admin per Hari</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[...filteredData.filteredHistory].reverse()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="tanggal" tickFormatter={(val) => val.substring(5)} fontSize={12} />
                    <YAxis tickFormatter={(val) => `Rp${(val/1000).toFixed(0)}K`} fontSize={12} />
                    <Tooltip formatter={(val) => formatRupiah(val)} />
                    <Line type="monotone" dataKey="total_admin_fee" stroke="#10b981" strokeWidth={2} dot={{fill: '#10b981'}} name="Fee Admin" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          {/* Summary Stats */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Rata-rata Omzet/Hari</p>
              <p className="font-bold text-purple-600">{formatRupiah(filteredData?.avgOmzet || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Rata-rata Fee/Hari</p>
              <p className="font-bold text-green-600">{formatRupiah(filteredData?.avgAdminFee || 0)}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Hari Aktif</p>
              <p className="font-bold text-blue-600">{filteredData?.filteredHistory?.length || 0} hari</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">Rata-rata Transaksi/Hari</p>
              <p className="font-bold text-indigo-600">{Math.round(filteredData?.totalTransaksi / (filteredData?.filteredHistory?.length || 1))} trx</p>
            </div>
          </div>

          {/* Cumulative Fee Admin per Bulan */}
          {filteredData?.monthlyHistory?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-600 mb-4">Akumulasi Fee Admin per Bulan</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={filteredData.monthlyHistory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="bulan"
                      tickFormatter={(val) => {
                        const [y, m] = val.split('-');
                        return `${m}/${y.slice(2)}`;
                      }}
                      fontSize={12}
                    />
                    <YAxis tickFormatter={(val) => `Rp${(val/1000).toFixed(0)}K`} fontSize={12} />
                    <Tooltip formatter={(val) => formatRupiah(val)} labelFormatter={(label) => `Bulan: ${label}`} />
                    <Bar
                      dataKey="kumulatif"
                      fill="#8b5cf6"
                      radius={[4, 4, 0, 0]}
                      name="Akumulasi Fee"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Per-bulan breakdown table */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {filteredData.monthlyHistory.map((m) => (
                  <div key={m.bulan} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">{m.bulan}</p>
                    <p className="text-sm font-semibold text-green-600">{formatRupiah(m.fee_admin)}</p>
                    <p className="text-xs text-gray-400 mt-1">Hari aktif: {m.hari_aktif}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tren Jumlah Transaksi per Nama Hari */}
          {filteredData?.dayOfWeekHistory?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-600 mb-4">Tren Jumlah Transaksi per Nama Hari</h4>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={filteredData.dayOfWeekHistory}
                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hari" fontSize={12} />
                    <YAxis tickFormatter={(val) => `${val}`} fontSize={12} />
                    <Tooltip
                      formatter={(val, name) => {
                        if (name === 'transaksi') return `${val} trx`;
                        if (name === 'omzet') return formatRupiah(val);
                        return val;
                      }}
                      labelFormatter={(label) => `Hari: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="transaksi" fill="#6366f1" radius={[4, 4, 0, 0]} name="Jumlah Transaksi" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {/* Per-hari breakdown cards sorted by transaction count */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {filteredData.dayOfWeekHistory.map((d, idx) => (
                  <div key={d.hari} className={`rounded-lg p-3 ${idx === 0 ? 'bg-green-50 border border-green-300' : 'bg-gray-50'}`}>
                    <p className="text-xs text-gray-500">{d.hari}</p>
                    <p className={`text-sm font-semibold ${idx === 0 ? 'text-green-700' : 'text-indigo-600'}`}>{d.transaksi} trx</p>
                    <p className="text-xs text-gray-400 mt-1">{d.count} hari aktif</p>
                    <p className="text-xs text-gray-400">Fee: {formatRupiah(d.fee)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Riwayat Laporan per Periode - Paginated */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-indigo-100 rounded-lg"><History className="text-indigo-600" size={24} /></div>
          <h3 className="font-semibold text-gray-900">Riwayat Laporan {filteredData?.periodLabel}</h3>
        </div>
        {(filteredData?.filteredHistory?.length > 0) ? (
          <>
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {filteredData.filteredHistory
                .slice((historyPage - 1) * historyPerPage, historyPage * historyPerPage)
                .map((day) => (
                  <div key={day.tanggal} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-28">
                          <p className="font-medium text-gray-900">{formatDate(day.tanggal)}</p>
                          <p className="text-xs text-gray-500">{day.jumlah_laporan} laporan</p>
                        </div>
                        <div className="hidden md:block w-px h-10 bg-gray-300"></div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Omzet</p>
                            <p className="font-semibold text-purple-600">{formatRupiah(day.total_omzet)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Fee Admin</p>
                            <p className="font-semibold text-green-600">{formatRupiah(day.total_admin_fee)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Transaksi</p>
                            <p className="font-semibold">{day.total_transaksi}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            {filteredData.filteredHistory.length > historyPerPage && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">Halaman {historyPage} / {Math.ceil(filteredData.filteredHistory.length / historyPerPage)}</p>
                <div className="flex gap-2">
                  <button disabled={historyPage === 1} onClick={() => setHistoryPage(p => p - 1)}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50">← Prev</button>
                  <button disabled={historyPage >= Math.ceil(filteredData.filteredHistory.length / historyPerPage)} onClick={() => setHistoryPage(p => p + 1)}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50">Next →</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">Belum ada laporan untuk periode ini</p>
          </div>
        )}
      </Card>

      {/* Cash vs Non-Cash & Bank Breakdown - Using filtered data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Cash vs Non-Cash */}
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Pembayaran</h3>
          {/* Show today's data if available, otherwise show latest report data */}
          {(t.total_omzet > 0) ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span> Tunai
              </span>
              <span className="font-semibold text-green-700">{formatRupiah(t.total_tunai || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span> Non-Tunai
              </span>
              <span className="font-semibold text-blue-700">{formatRupiah(t.total_non_tunai || 0)}</span>
            </div>
            <div className="h-px bg-gray-200 my-2"></div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Total</span>
              <span className="font-bold text-primary-600">{formatRupiah(t.total_omzet || 0)}</span>
            </div>
          </div>
          ) : filteredData?.latestReport ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span> Tunai
              </span>
              <span className="font-semibold text-green-700">{formatRupiah(filteredData.latestReport.total_tunai || filteredData.latestReport.total_omzet || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span> Non-Tunai
              </span>
              <span className="font-semibold text-blue-700">{formatRupiah(filteredData.latestReport.total_non_tunai || 0)}</span>
            </div>
            <div className="h-px bg-gray-200 my-2"></div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Total</span>
              <span className="font-bold text-primary-600">{formatRupiah(filteredData.latestReport.total_omzet || 0)}</span>
            </div>
            <p className="text-xs text-gray-400 mt-2">Data dari laporan terakhir: {formatDate(filteredData.latestReport.tanggal)}</p>
          </div>
          ) : (
          <div className="text-center py-4 text-gray-400">
            <p>Tidak ada data pembayaran</p>
          </div>
          )}
        </Card>

        {/* Bank Breakdown */}
        <Card className="p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Per Bank</h3>
          {/* Show today's data if available, otherwise show from latest report */}
          {(t.total_bsi > 0 || t.total_mandiri > 0) ? (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-600"></span> BSI
              </span>
              <span className="font-semibold text-blue-700">{formatRupiah(t.total_bsi || 0)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span> Mandiri
              </span>
              <span className="font-semibold text-red-600">{formatRupiah(t.total_mandiri || 0)}</span>
            </div>
            <div className="h-px bg-gray-200 my-2"></div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Total</span>
              <span className="font-bold text-primary-600">{formatRupiah(t.total_omzet || 0)}</span>
            </div>
          </div>
          ) : filteredData?.latestReport?.bank_summaries ? (
          <div className="space-y-3">
            {filteredData.latestReport.bank_summaries.map((bank, idx) => (
              <div key={idx} className="flex justify-between items-center">
                <span className="text-gray-600 flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${bank.bank_id === 'BRIS' ? 'bg-blue-600' : 'bg-red-500'}`}></span>
                  {bank.bank_nama}
                </span>
                <span className="font-semibold">{formatRupiah(bank.total_tagihan || 0)}</span>
              </div>
            ))}
            <div className="h-px bg-gray-200 my-2"></div>
            <div className="flex justify-between items-center">
              <span className="font-medium">Total</span>
              <span className="font-bold text-primary-600">{formatRupiah(filteredData.latestReport.total_tagihan || 0)}</span>
            </div>
            <p className="text-xs text-gray-400 mt-2">Data dari laporan terakhir: {formatDate(filteredData.latestReport.tanggal)}</p>
          </div>
          ) : (
          <div className="text-center py-4 text-gray-400">
            <p>Tidak ada data bank</p>
          </div>
          )}
        </Card>
      </div>

      {/* Laporan Hari Ini - Detail */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-4">
          {loketPeriod === 'today' ? 'Laporan Hari Ini' : 'Laporan Periode Terpilih'}
        </h3>
        {filteredData?.filteredHistory?.length > 0 ? (
          <>
            <div className="space-y-4">
              {filteredData.filteredHistory
                .slice((historyPage - 1) * historyPerPage, historyPage * historyPerPage)
                .map((day, idx) => (
                <div key={day.tanggal} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-100 px-4 py-2 flex justify-between items-center">
                    <span className="font-medium">{formatDate(day.tanggal)}</span>
                    <span className="text-sm text-gray-500">{day.jumlah_laporan} laporan</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {day.reports?.map(lap => (
                      <div key={lap.id} className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{lap.shift_period}</p>
                            <p className="text-sm text-gray-500">{lap.total_transaksi} transaksi</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-primary-600">{formatRupiah(lap.total_omzet)}</p>
                            <StatusBadge status={lap.status} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {filteredData.filteredHistory.length > historyPerPage && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">Halaman {historyPage} / {Math.ceil(filteredData.filteredHistory.length / historyPerPage)}</p>
                <div className="flex gap-2">
                  <button
                    disabled={historyPage === 1}
                    onClick={() => setHistoryPage(p => p - 1)}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >← Prev</button>
                  <button
                    disabled={historyPage >= Math.ceil(filteredData.filteredHistory.length / historyPerPage)}
                    onClick={() => setHistoryPage(p => p + 1)}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >Next →</button>
                </div>
              </div>
            )}
          </>
        ) : filteredData?.latestReport ? (
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="font-medium text-lg">{filteredData.latestReport.shift_period}</p>
                <p className="text-sm text-gray-500">{filteredData.latestReport.total_transaksi} transaksi</p>
                <p className="text-xs text-gray-400 mt-1">Tanggal: {formatDate(filteredData.latestReport.tanggal)}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-primary-600 text-lg">{formatRupiah(filteredData.latestReport.total_omzet)}</p>
                <StatusBadge status={filteredData.latestReport.status} />
              </div>
            </div>
            {/* Rincian dari latest report */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div className="bg-white p-2 rounded">
                <p className="text-gray-400 text-xs">BSI</p>
                <p className="font-medium">{formatRupiah(filteredData.latestReport.total_bsi || 0)}</p>
              </div>
              <div className="bg-white p-2 rounded">
                <p className="text-gray-400 text-xs">Mandiri</p>
                <p className="font-medium">{formatRupiah(filteredData.latestReport.total_mandiri || 0)}</p>
              </div>
              <div className="bg-white p-2 rounded">
                <p className="text-gray-400 text-xs">Admin Fee</p>
                <p className="font-medium">{formatRupiah(filteredData.latestReport.total_admin_fee || 0)}</p>
              </div>
              <div className="bg-white p-2 rounded">
                <p className="text-gray-400 text-xs">Uang Kecil</p>
                <p className="font-medium">{formatRupiah(filteredData.latestReport.uang_kecil || 0)}</p>
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-3">
              <span className="font-medium">Catatan:</span> Menampilkan laporan terakhir karena belum ada laporan untuk periode ini
            </p>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">Belum ada laporan</p>
        )}
      </Card>

      {/* Riwayat Laporan Terbaru */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Riwayat Laporan Terbaru</h3>
        {allReports.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Transaksi</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Omzet</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {allReports
                    .slice((riwayatPage - 1) * riwayatPerPage, riwayatPage * riwayatPerPage)
                    .map(lap => (
                    <tr key={lap.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{formatDate(lap.tanggal)}</td>
                      <td className="px-4 py-3 text-sm">{lap.shift_period}</td>
                      <td className="px-4 py-3 text-sm text-right">{lap.total_transaksi}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{formatRupiah(lap.total_omzet)}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={lap.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {allReports.length > riwayatPerPage && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">Halaman {riwayatPage} / {Math.ceil(allReports.length / riwayatPerPage)}</p>
                <div className="flex gap-2">
                  <button
                    disabled={riwayatPage === 1}
                    onClick={() => setRiwayatPage(p => p - 1)}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >← Prev</button>
                  <button
                    disabled={riwayatPage >= Math.ceil(allReports.length / riwayatPerPage)}
                    onClick={() => setRiwayatPage(p => p + 1)}
                    className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                  >Next →</button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500 text-center py-8">Belum ada laporan</p>
        )}
      </Card>
    </div>
  );
};

// ==================== LAPORAN SHIFT (LOKET) ====================

// Import dual bank component
import LaporanShiftDualBankV2 from './LaporanShiftDualBankV2';
import RiwayatLoketPage from './RiwayatLoketPage';
import TechnicalJobsPage from './TechnicalJobsPage';

// ==================== LAPORAN KASIR HARIAN ====================

// Import cashier report components
import LaporanKasirDetail from './LaporanKasirDetail';
import RiwayatLaporanKasirDetail from './RiwayatLaporanKasirDetail';

const LaporanShiftPage = ({ currentUser }) => {
  const [providers, setProviders] = useState([]);
  const [shiftSettings, setShiftSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdLaporan, setCreatedLaporan] = useState(null);
  
  const [formData, setFormData] = useState({
    shift_period: 'PAGI',
    saldo_awal: '',
    saldo_akhir: '',
    total_tunai: '',
    total_non_tunai: '',
    uang_kecil: '',
    catatan: ''
  });
  
  const [transaksiList, setTransaksiList] = useState([]);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [provs, shifts] = await Promise.all([
        api.get('/api/providers'),
        api.get('/api/settings/shifts')
      ]);
      setProviders(provs);
      setShiftSettings(shifts.filter(s => s.is_active));
      // Initialize transaksi list with providers
      setTransaksiList(provs.filter(p => p.is_active).map(p => ({
        provider_id: p.id,
        provider_name: p.nama_provider,
        biaya_admin: p.biaya_admin,
        tipe_transaksi: p.tipe_transaksi || 'TUNAI',
        jumlah_transaksi: 0,
        total_tagihan: 0
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateTransaksi = (providerId, field, value) => {
    setTransaksiList(prev => prev.map(t => {
      if (t.provider_id === providerId) {
        const updated = { ...t, [field]: parseFloat(value) || 0 };
        // Auto calculate admin fee
        if (field === 'jumlah_transaksi') {
          updated.total_admin_fee = updated.jumlah_transaksi * t.biaya_admin;
        }
        return updated;
      }
      return t;
    }));
  };

  const calculateTotals = () => {
    const activeTrx = transaksiList.filter(t => t.jumlah_transaksi > 0);
    return {
      totalTransaksi: activeTrx.reduce((s, t) => s + t.jumlah_transaksi, 0),
      totalTagihan: activeTrx.reduce((s, t) => s + t.total_tagihan, 0),
      totalAdminFee: activeTrx.reduce((s, t) => s + (t.jumlah_transaksi * t.biaya_admin), 0)
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser?.loket_id) {
      setError('Anda tidak terdaftar di loket manapun');
      return;
    }
    
    const activeTrx = transaksiList.filter(t => t.jumlah_transaksi > 0);
    if (activeTrx.length === 0) {
      setError('Mohon input minimal 1 transaksi');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const laporan = await api.post('/api/laporan-shift', {
        loket_id: currentUser.loket_id,
        shift_period: formData.shift_period,
        saldo_awal: parseFloat(formData.saldo_awal) || 0,
        saldo_akhir: parseFloat(formData.saldo_akhir) || 0,
        transaksi_list: activeTrx.map(t => ({
          provider_id: t.provider_id,
          jumlah_transaksi: t.jumlah_transaksi,
          total_tagihan: t.total_tagihan,
          total_admin_fee: t.jumlah_transaksi * t.biaya_admin
        })),
        total_tunai: parseFloat(formData.total_tunai) || 0,
        total_non_tunai: parseFloat(formData.total_non_tunai) || 0,
        uang_kecil: parseFloat(formData.uang_kecil) || 0,
        catatan: formData.catatan
      });
      
      setCreatedLaporan(laporan);
      setSuccess('Laporan shift berhasil dibuat! Silakan upload bukti transaksi.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadBukti = async (file) => {
    if (!createdLaporan) return;
    setUploading(true);
    try {
      await api.uploadFile(`/api/laporan-shift/${createdLaporan.id}/upload-bukti`, file);
      const updated = await api.get(`/api/laporan-shift/${createdLaporan.id}`);
      setCreatedLaporan(updated);
      setSuccess('Bukti berhasil diupload!');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ shift_period: 'PAGI', saldo_awal: '', saldo_akhir: '', total_tunai: '', total_non_tunai: '', uang_kecil: '', catatan: '' });
    setTransaksiList(providers.map(p => ({ provider_id: p.id, provider_name: p.nama_provider, biaya_admin: p.biaya_admin, jumlah_transaksi: 0, total_tagihan: 0 })));
    setCreatedLaporan(null);
    setSuccess('');
  };

  const totals = calculateTotals();

  if (loading) return <LoadingSpinner size="lg" />;

  // Show success state with upload option
  if (createdLaporan) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Laporan Shift Berhasil Dibuat</h1>
          <Button onClick={resetForm}><Plus size={18} /> Buat Laporan Baru</Button>
        </div>

        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="text-green-500" size={24} />
            <h3 className="font-semibold text-gray-900">Ringkasan Laporan</h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Nomor Laporan</p>
              <p className="font-semibold">{createdLaporan.nomor_laporan}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Shift</p>
              <p className="font-semibold">{createdLaporan.shift_period}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Total Transaksi</p>
              <p className="font-semibold">{createdLaporan.total_transaksi}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Total Omzet</p>
              <p className="font-semibold text-primary-600">{formatRupiah(createdLaporan.total_omzet)}</p>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium text-gray-900 mb-3">Upload Bukti Transaksi</h4>
            <div className="flex items-center gap-4">
              <FileUpload onUpload={handleUploadBukti} uploading={uploading} />
              <span className="text-sm text-gray-500">Foto struk, rekap harian, atau bukti lainnya</span>
            </div>
            
            {createdLaporan.bukti_urls?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">Bukti yang sudah diupload:</p>
                <BuktiGallery urls={createdLaporan.bukti_urls} />
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Buat Laporan Shift</h1>
          <p className="text-gray-500">Input jumlah transaksi per provider</p>
        </div>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Shift Info */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Informasi Shift</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select
              label="Periode Shift"
              value={formData.shift_period}
              onChange={(e) => setFormData({ ...formData, shift_period: e.target.value })}
              options={shiftSettings.map(s => ({
                value: s.shift_name,
                label: `${s.shift_name} (${s.jam_mulai} - ${s.jam_selesai})`
              }))}
            />
            <Input label="Saldo Awal Kas (Rp)" type="number" value={formData.saldo_awal} onChange={(e) => setFormData({ ...formData, saldo_awal: e.target.value })} placeholder="0" required />
            <Input label="Saldo Akhir Kas (Rp)" type="number" value={formData.saldo_akhir} onChange={(e) => setFormData({ ...formData, saldo_akhir: e.target.value })} placeholder="0" required />
            <Input label="Uang Kecil / Kembalian (Rp)" type="number" value={formData.uang_kecil} onChange={(e) => setFormData({ ...formData, uang_kecil: e.target.value })} placeholder="0" />
          </div>
        </Card>

        {/* Transaksi per Provider */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Transaksi per Provider</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tipe</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Jumlah Transaksi</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Tagihan (Rp)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Admin/Trx</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transaksiList.map(trx => (
                  <tr key={trx.provider_id} className={trx.jumlah_transaksi > 0 ? 'bg-green-50' : ''}>
                    <td className="px-4 py-3 text-sm font-medium">{trx.provider_name}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${trx.tipe_transaksi === 'TUNAI' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {trx.tipe_transaksi === 'TUNAI' ? '💵' : '💳'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <input type="number" min="0" value={trx.jumlah_transaksi || ''} onChange={(e) => updateTransaksi(trx.provider_id, 'jumlah_transaksi', e.target.value)} className="w-24 px-2 py-1 border rounded text-center mx-auto block" placeholder="0" />
                    </td>
                    <td className="px-4 py-3">
                      <input type="number" min="0" value={trx.total_tagihan || ''} onChange={(e) => updateTransaksi(trx.provider_id, 'total_tagihan', e.target.value)} className="w-32 px-2 py-1 border rounded text-right mx-auto block" placeholder="0" disabled={trx.jumlah_transaksi === 0} />
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-500">{formatRupiah(trx.biaya_admin)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium">{formatRupiah(trx.jumlah_transaksi * trx.biaya_admin)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-3 text-right" colSpan="2">TOTAL:</td>
                  <td className="px-4 py-3 text-center">{totals.totalTransaksi}</td>
                  <td className="px-4 py-3 text-right">{formatRupiah(totals.totalTagihan)}</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right text-primary-600">{formatRupiah(totals.totalAdminFee)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        {/* Total Pembayaran */}
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Total Penerimaan</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Total Penerimaan TUNAI (Rp)" type="number" value={formData.total_tunai} onChange={(e) => setFormData({ ...formData, total_tunai: e.target.value })} placeholder="0" required />
            <Input label="Total Penerimaan NON-TUNAI (Rp)" type="number" value={formData.total_non_tunai} onChange={(e) => setFormData({ ...formData, total_non_tunai: e.target.value })} placeholder="0" />
          </div>
          <div className="mt-4 p-4 bg-primary-50 rounded-lg">
            <div className="flex justify-between text-lg font-semibold">
              <span>Total Omzet:</span>
              <span className="text-primary-600">{formatRupiah((parseFloat(formData.total_tunai) || 0) + (parseFloat(formData.total_non_tunai) || 0))}</span>
            </div>
          </div>
        </Card>

        {/* Catatan */}
        <Card className="p-5">
          <Input label="Catatan (Opsional)" value={formData.catatan} onChange={(e) => setFormData({ ...formData, catatan: e.target.value })} placeholder="Catatan tambahan untuk shift ini" />
        </Card>

        <Button type="submit" className="w-full" size="lg" loading={submitting}>
          <Save size={20} /> Simpan Laporan Shift
        </Button>
      </form>
    </div>
  );
};

// ==================== DASHBOARD KASIR ====================

const DashboardKasir = ({ currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kasirPeriod, setKasirPeriod] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [feeAdminPage, setFeeAdminPage] = useState(1);
  const [feeAdminPerPage] = useState(10);

  useEffect(() => { setFeeAdminPage(1); }, [kasirPeriod, customDateRange.start, customDateRange.end]);
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.get('/api/dashboard/kasir');
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  // Dynamic title based on role
  const getDashboardTitle = () => {
    if (currentUser.role === 'SUPERKASIR') return 'Dashboard Superkasir';
    if (currentUser.role === 'MANAGER' || currentUser.role === 'OWNER') return 'Dashboard Kasir & Keuangan';
    return 'Dashboard Kasir';
  };

  // Helper function to get date range based on period
  const getDateRange = () => {
    const today = new Date();
    const start = new Date(today);

    switch (kasirPeriod) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case '7days':
        start.setDate(today.getDate() - 6);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case '30days':
        start.setDate(today.getDate() - 29);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case 'month':
        start.setDate(1);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
      case 'all':
        return { start: '2020-01-01', end: today.toISOString().split('T')[0] };
      case 'custom':
        return { start: customDateRange.start, end: customDateRange.end };
      default:
        start.setDate(today.getDate() - 6);
        return { start: start.toISOString().split('T')[0], end: today.toISOString().split('T')[0] };
    }
  };

  // Calculate filtered data based on selected period - combines laporan_shift AND cashier_reports
  const calculateFilteredData = () => {
    if (!data) return null;

    const { start, end } = getDateRange();

    // Filter cashier_history by date range
    const cashierFiltered = (data.cashier_history || []).filter(day => {
      return day.tanggal >= start && day.tanggal <= end;
    });

    // Filter laporan_shift (loket) history by date range
    const loketFiltered = (data.admin_fee_summary?.history || []).filter(day => {
      return day.tanggal >= start && day.tanggal <= end;
    });

    // Calculate totals from cashier reports
    const cashierTotalSetoran = cashierFiltered.reduce((sum, day) => sum + (day.total_setoran || 0), 0);
    const cashierTotalAdmin = cashierFiltered.reduce((sum, day) => sum + (day.total_admin || 0), 0);
    const cashierTotalLaporan = cashierFiltered.reduce((sum, day) => sum + (day.jumlah_laporan || 0), 0);

    // Calculate totals from loket reports
    const loketTotalOmzet = loketFiltered.reduce((sum, day) => sum + (day.total_omzet || 0), 0);
    const loketTotalAdmin = loketFiltered.reduce((sum, day) => sum + (day.total_admin_fee || 0), 0);
    const loketTotalLaporan = loketFiltered.reduce((sum, day) => sum + (day.jumlah_laporan || 0), 0);

    // Combined totals
    const totalPemasukan = cashierTotalAdmin + loketTotalAdmin; // Fee admin is the income
    const totalSetoran = cashierTotalSetoran;
    const totalOmzet = loketTotalOmzet;
    const totalFeeAdmin = cashierTotalAdmin + loketTotalAdmin;
    const totalLaporan = cashierTotalLaporan + loketTotalLaporan;

    // Today's data for pengeluaran display
    const todayStr = new Date().toISOString().split('T')[0];
    const todayPengeluaran = (data.pengeluaran_today || []).filter(p => p.tanggal === todayStr);
    const todayPengeluaranTotal = todayPengeluaran.reduce((sum, p) => sum + (p.status === 'VERIFIED' ? p.jumlah : 0), 0);

    // Biaya Operasional dari form Pengeluaran (VERIFIED only)
    const totalBiayaOperasional = todayPengeluaran
      .filter(p => p.status === 'VERIFIED')
      .reduce((sum, p) => sum + (p.jumlah || 0), 0);

    // Calculate total pengeluaran for period (from cashier_reports: kas kecil + superpel)
    const totalPenguranganKasKecil = cashierFiltered.reduce((sum, day) =>
      sum + (day.total_pengurangan_kas_kecil || 0), 0);
    const totalSuperpel = cashierFiltered.reduce((sum, day) =>
      sum + (day.superpel || 0), 0);
    const totalPengeluaranKasKecil = totalPenguranganKasKecil + totalSuperpel;
    const totalPengeluaran = totalPengeluaranKasKecil;

    // Total Penerimaan Kas Kecil (period)
    const totalPenerimaanKasKecil = cashierFiltered.reduce((sum, day) => {
      const daily = (day.reports || []).reduce((s, r) => s + (r.penerimaan_kas_kecil || 0), 0);
      return sum + daily;
    }, 0);

    // Breakdown for professional accounting format (per form/jenis)
    const totalPemasukanAccounting = loketTotalAdmin + cashierTotalAdmin;
    const totalPengeluaranAccounting = totalBiayaOperasional + totalPenguranganKasKecil + totalSuperpel;
    const saldoBersihAccounting = totalPemasukanAccounting - totalPengeluaranAccounting;

    // Calculate average per day
    const daysCount = Math.max(cashierFiltered.length, loketFiltered.length, 1);
    const avgPemasukan = totalPemasukan / daysCount;
    const avgPengeluaran = (data?.summary?.total_pengeluaran || 0) / daysCount;

    // Pengeluaran by type for the period
    const pengeluaranByType = {};
    todayPengeluaran.forEach(p => {
      const jenis = p.jenis || 'LAINNYA';
      if (!pengeluaranByType[jenis]) pengeluaranByType[jenis] = 0;
      pengeluaranByType[jenis] += p.jumlah;
    });

    // Collect all reports
    const cashierReports = cashierFiltered.flatMap(day => day.reports || []);
    const loketReports = loketFiltered.flatMap(day => day.reports || []);

    // Pengelompokan jenis form
    const kelompokPemasukan = [
      { nama: 'Form Laporan Shift (Loket)', count: loketTotalLaporan, omzet: loketTotalOmzet, fee: loketTotalAdmin },
      { nama: 'Form Laporan Kasir', count: cashierTotalLaporan, setoran: totalSetoran, fee: cashierTotalAdmin }
    ];
    const kelompokPengeluaran = [
      { nama: 'Saldo Operasional (Kas Kecil)', jenis: 'OPERASIONAL', reduction: totalPenguranganKasKecil },
      { nama: 'Petty Cash (Superpel/Alat Pel)', jenis: 'PETTY_CASH', reduction: totalSuperpel },
      { nama: 'Biaya Operasional (Form Pengeluaran)', jenis: 'BIAYA_OPERASIONAL', reduction: totalBiayaOperasional }
    ];

    return {
      cashierFiltered,
      loketFiltered,
      totalPemasukan,
      totalSetoran,
      totalOmzet,
      totalFeeAdmin,
      cashierTotalAdmin,
      cashierTotalSetoran,
      loketTotalAdmin,
      loketTotalOmzet,
      totalLaporan,
      cashierReports,
      loketReports,
      avgPemasukan,
      totalPengeluaran,
      totalSuperpel,
      totalPenguranganKasKecil,
      totalBiayaOperasional,
      totalPenerimaanKasKecil,
      totalPemasukanAccounting,
      totalPengeluaranAccounting,
      saldoBersihAccounting,
      kelompokPemasukan,
      kelompokPengeluaran,
      avgPengeluaran,
      todayPengeluaran,
      todayPengeluaranTotal,
      pengeluaranByType,
      daysCount,
      periodLabel: kasirPeriod === 'today' ? 'Hari Ini' :
                   kasirPeriod === '7days' ? '7 Hari Terakhir' :
                   kasirPeriod === '30days' ? '30 Hari Terakhir' :
                   kasirPeriod === 'month' ? 'Bulan Ini' :
                   kasirPeriod === 'all' ? 'Semua Data' :
                   kasirPeriod === 'custom' ? `${formatDate(customDateRange.start)} - ${formatDate(customDateRange.end)}` : '7 Hari Terakhir',
      dateRange: { start, end }
    };
  };

  const filteredData = calculateFilteredData();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{getDashboardTitle()}</h1>
          {(currentUser?.role === 'OWNER' || currentUser?.role === 'MANAGER' || currentUser?.role === 'SUPERKASIR') && (
            <p className="text-sm text-indigo-600 font-medium mt-1">
              📊 Menampilkan data seluruh kasir & loket
            </p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={loadData}><RefreshCw size={16} /> Refresh</Button>
      </div>

      {/* Alert for pending */}
      {data?.pending_count > 0 && (
        <Alert type="warning" message={`Ada ${data.pending_count} laporan loket menunggu verifikasi`} />
      )}

      {/* Period Selector - Prominent */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Calendar className="text-indigo-600" size={24} />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Ringkasan Keuangan</h2>
              <p className="text-sm text-gray-500">
                Periode: <span className="font-medium text-indigo-600">{filteredData?.periodLabel}</span>
                {filteredData?.dateRange && (
                  <span className="text-gray-400"> ({formatDate(filteredData.dateRange.start)} - {formatDate(filteredData.dateRange.end)})</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
              value={kasirPeriod}
              onChange={(e) => setKasirPeriod(e.target.value)}
            >
              <option value="today">Hari Ini</option>
              <option value="7days">7 Hari Terakhir</option>
              <option value="30days">30 Hari Terakhir</option>
              <option value="month">Bulan Ini</option>
              <option value="all">Semua Data</option>
              <option value="custom">Pilih Tanggal</option>
            </select>

            {kasirPeriod === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                />
                <span className="text-gray-500">-</span>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ===== SUMMARY CARDS - PROFESSIONAL FORMAT ===== */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* TOTAL PEMASUKAN */}
        <div className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl border-2 border-emerald-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <ArrowDownLeft className="text-emerald-600" size={24} />
            </div>
            <span className="text-xs font-medium text-emerald-600 bg-emerald-200 px-2 py-1 rounded-full">
              {filteredData?.periodLabel}
            </span>
          </div>
          <p className="text-xs text-emerald-700 font-medium uppercase tracking-wide">Total Pemasukan</p>
          <p className="text-3xl font-bold text-emerald-800 mt-1">{formatRupiah(filteredData?.totalPemasukan || 0)}</p>
          <p className="text-xs text-emerald-600 mt-2">
            Fee Loket: {formatRupiah(filteredData?.loketTotalAdmin || 0)}
          </p>
          <p className="text-xs text-emerald-600">
            Fee Kasir: {formatRupiah(filteredData?.cashierTotalAdmin || 0)}
          </p>
        </div>

        {/* TOTAL PENGELUARAN */}
        <div className="p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl border-2 border-red-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-red-100 rounded-xl">
              <ArrowUpRight className="text-red-600" size={24} />
            </div>
            <span className="text-xs font-medium text-red-600 bg-red-200 px-2 py-1 rounded-full">
              Operasional
            </span>
          </div>
          <p className="text-xs text-red-700 font-medium uppercase tracking-wide">Total Pengeluaran</p>
          <p className="text-3xl font-bold text-red-800 mt-1">{formatRupiah(filteredData?.totalPengeluaran || 0)}</p>
          <p className="text-xs text-red-600 mt-2">
            Saldo Operasional: {formatRupiah(data?.summary?.total_kas_kecil || 0)}
          </p>
          <p className="text-xs text-red-600">
            Kas Kecil (Parkir/Pel): {formatRupiah(data?.summary?.total_pengurangan_kas_kecil || 0)}
          </p>
        </div>

        {/* SALDO BERSIH */}
        <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border-2 border-blue-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Banknote className="text-blue-600" size={24} />
            </div>
            <span className="text-xs font-medium text-blue-600 bg-blue-200 px-2 py-1 rounded-full">
              {filteredData?.periodLabel}
            </span>
          </div>
          <p className="text-xs text-blue-700 font-medium uppercase tracking-wide">Saldo Bersih</p>
          <p className="text-3xl font-bold text-blue-800 mt-1">{formatRupiah((filteredData?.totalPemasukan || 0) - (filteredData?.totalPengeluaran || 0))}</p>
          <p className="text-xs text-blue-600 mt-2">
            Pemasukan - Pengeluaran
          </p>
          <div className="mt-3 flex gap-2">
            <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-1 rounded">
              +{formatRupiah(filteredData?.totalPemasukan || 0)}
            </span>
            <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
              -{formatRupiah(filteredData?.totalPengeluaran || 0)}
            </span>
          </div>
        </div>

        {/* JUMLAH LAPORAN */}
        <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border-2 border-purple-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <ClipboardList className="text-purple-600" size={24} />
            </div>
            <span className="text-xs font-medium text-purple-600 bg-purple-200 px-2 py-1 rounded-full">
              Laporan
            </span>
          </div>
          <p className="text-xs text-purple-700 font-medium uppercase tracking-wide">Total Laporan</p>
          <p className="text-3xl font-bold text-purple-800 mt-1">{filteredData?.totalLaporan || 0}</p>
          <p className="text-xs text-purple-600 mt-2">
            Kasir: <span className="font-semibold">{filteredData?.cashierFiltered?.length || 0}</span> |
            Loket: <span className="font-semibold">{filteredData?.loketFiltered?.length || 0}</span>
          </p>
        </div>
      </div>

      {/* ===== ANALYTICS CHARTS ===== */}
      {(filteredData?.cashierFiltered?.length > 0 || filteredData?.loketFiltered?.length > 0) && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-xl">
                <TrendingUp className="text-indigo-600" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Tren Pemasukan & Pengeluaran</h3>
                <p className="text-sm text-gray-500">Periode: {filteredData?.periodLabel}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="px-3 py-1.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full border border-emerald-200">
                Pemasukan
              </span>
              <span className="px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded-full border border-red-200">
                Pengeluaran
              </span>
            </div>
          </div>

          {/* Dual Chart: Pemasukan vs Pengeluaran */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pemasukan Chart - Fee Admin from Loket */}
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2 mb-4">
                <ArrowDownLeft className="text-emerald-600" size={20} />
                <h4 className="text-sm font-semibold text-emerald-800">Tren Pemasukan (Fee Admin Loket)</h4>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...filteredData.loketFiltered].sort((a, b) => a.tanggal.localeCompare(b.tanggal))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="tanggal" tickFormatter={(val) => val.substring(5)} fontSize={11} />
                    <YAxis tickFormatter={(val) => `Rp${(val/1000).toFixed(0)}K`} fontSize={11} />
                    <Tooltip formatter={(val) => formatRupiah(val)} labelFormatter={(label) => `Tanggal: ${label}`} />
                    <Bar dataKey="total_admin_fee" fill="#059669" name="Fee Admin" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pengeluaran Chart - Operational Expenses */}
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
              <div className="flex items-center gap-2 mb-4">
                <ArrowUpRight className="text-red-600" size={20} />
                <h4 className="text-sm font-semibold text-red-800">Tren Pengeluaran Operasional</h4>
              </div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...filteredData.cashierFiltered].sort((a, b) => a.tanggal.localeCompare(b.tanggal))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="tanggal" tickFormatter={(val) => val.substring(5)} fontSize={11} />
                    <YAxis tickFormatter={(val) => `Rp${(val/1000).toFixed(0)}K`} fontSize={11} />
                    <Tooltip formatter={(val) => formatRupiah(val)} labelFormatter={(label) => `Tanggal: ${label}`} />
                    <Bar dataKey="total_pengurangan_kas_kecil" fill="#dc2626" name="Pengeluaran Kas Kecil" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="superpel" fill="#ef4444" name="Superpel" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* REKAP AKUNTANSI PROFESIONAL */}
          <div className="mt-6 space-y-4">
            {/* HEADER REKAP */}
            <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-lg">REKAPITULASI KEUANGAN</h4>
                  <p className="text-sm text-gray-300">Periode: {filteredData?.periodLabel}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Tanggal Cetak</p>
                  <p className="text-sm">{new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                </div>
              </div>
            </div>

            {/* PEMASUKAN GROUP */}
            <div className="border border-emerald-300 rounded-xl overflow-hidden">
              <div className="bg-emerald-600 text-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <ArrowDownLeft size={20} />
                  <h4 className="font-bold">I. PEMASUKAN</h4>
                </div>
              </div>
              <div className="p-4 bg-emerald-50">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-2">
                  <div className="col-span-5">Nama Form / Sumber</div>
                  <div className="col-span-2 text-right">Jumlah Laporan</div>
                  <div className="col-span-2 text-right">Total Omzet/Setoran</div>
                  <div className="col-span-3 text-right">Fee Admin (Pendapatan)</div>
                </div>
                {/* Row 1: Laporan Shift */}
                <div className="grid grid-cols-12 gap-2 text-sm px-2 py-2 bg-white rounded mb-1">
                  <div className="col-span-5 flex items-center text-gray-700">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    Form Laporan Shift (Loket)
                  </div>
                  <div className="col-span-2 text-right font-medium">{filteredData?.loketFiltered?.length || 0}</div>
                  <div className="col-span-2 text-right">{formatRupiah(filteredData?.loketTotalOmzet || 0)}</div>
                  <div className="col-span-3 text-right font-bold text-emerald-700">+{formatRupiah(filteredData?.loketTotalAdmin || 0)}</div>
                </div>
                {/* Row 2: Laporan Kasir */}
                <div className="grid grid-cols-12 gap-2 text-sm px-2 py-2 bg-white rounded mb-2">
                  <div className="col-span-5 flex items-center text-gray-700">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                    Form Laporan Kasir
                  </div>
                  <div className="col-span-2 text-right font-medium">{filteredData?.cashierFiltered?.length || 0}</div>
                  <div className="col-span-2 text-right">{formatRupiah(filteredData?.cashierTotalSetoran || 0)}</div>
                  <div className="col-span-3 text-right font-bold text-emerald-700">+{formatRupiah(filteredData?.cashierTotalAdmin || 0)}</div>
                </div>
                {/* Total Pemasukan */}
                <div className="grid grid-cols-12 gap-2 text-sm px-2 py-3 bg-emerald-100 rounded border-t-2 border-emerald-300 font-bold">
                  <div className="col-span-5 text-gray-800">TOTAL PEMASUKAN</div>
                  <div className="col-span-2 text-right text-gray-800">{filteredData?.totalLaporan || 0}</div>
                  <div className="col-span-2 text-right text-gray-800">{formatRupiah(filteredData?.totalOmzet + filteredData?.cashierTotalSetoran || 0)}</div>
                  <div className="col-span-3 text-right text-emerald-800">{formatRupiah(filteredData?.totalPemasukanAccounting || filteredData?.totalPemasukan || 0)}</div>
                </div>
              </div>
            </div>

            {/* PENGELUARAN GROUP */}
            <div className="border border-red-300 rounded-xl overflow-hidden">
              <div className="bg-red-600 text-white px-4 py-3">
                <div className="flex items-center gap-2">
                  <ArrowUpRight size={20} />
                  <h4 className="font-bold">II. PENGELUARAN</h4>
                </div>
              </div>
              <div className="p-4 bg-red-50">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 px-2">
                  <div className="col-span-5">Nama Form / Pengeluaran</div>
                  <div className="col-span-3 text-right">Saldo/Penerimaan</div>
                  <div className="col-span-2 text-right">Pengurangan</div>
                  <div className="col-span-2 text-right">Saldo Akhir</div>
                </div>
                {/* Row 1: Saldo Operasional */}
                <div className="grid grid-cols-12 gap-2 text-sm px-2 py-2 bg-white rounded mb-1">
                  <div className="col-span-5 flex items-center text-gray-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Saldo Operasional (Kas Kecil)
                  </div>
                  <div className="col-span-3 text-right text-gray-600">{formatRupiah(data?.summary?.total_kas_kecil || 0)}</div>
                  <div className="col-span-2 text-right text-red-600">-{formatRupiah(filteredData?.totalPenguranganKasKecil || 0)}</div>
                  <div className="col-span-2 text-right font-medium">{formatRupiah((data?.summary?.total_kas_kecil || 0) - (filteredData?.totalPenguranganKasKecil || 0))}</div>
                </div>
                {/* Row 2: Petty Cash */}
                <div className="grid grid-cols-12 gap-2 text-sm px-2 py-2 bg-white rounded mb-2">
                  <div className="col-span-5 flex items-center text-gray-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Petty Cash (Superpel/Alat Pel)
                  </div>
                  <div className="col-span-3 text-right text-gray-400">-</div>
                  <div className="col-span-2 text-right text-red-600">-{formatRupiah(filteredData?.totalSuperpel || 0)}</div>
                  <div className="col-span-2 text-right font-medium text-red-600">-{formatRupiah(filteredData?.totalSuperpel || 0)}</div>
                </div>
                {/* Row 3: Biaya Operasional */}
                <div className="grid grid-cols-12 gap-2 text-sm px-2 py-2 bg-white rounded mb-2">
                  <div className="col-span-5 flex items-center text-gray-700">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Biaya Operasional (Form Pengeluaran)
                  </div>
                  <div className="col-span-3 text-right text-gray-400">-</div>
                  <div className="col-span-2 text-right text-red-600">-{formatRupiah(filteredData?.totalBiayaOperasional || 0)}</div>
                  <div className="col-span-2 text-right font-medium text-red-600">-{formatRupiah(filteredData?.totalBiayaOperasional || 0)}</div>
                </div>
                {/* Total Pengeluaran */}
                <div className="grid grid-cols-12 gap-2 text-sm px-2 py-3 bg-red-100 rounded border-t-2 border-red-300 font-bold">
                  <div className="col-span-5 text-gray-800">TOTAL PENGELUARAN</div>
                  <div className="col-span-3 text-right text-gray-800">-</div>
                  <div className="col-span-2 text-right text-red-800">{formatRupiah(filteredData?.totalPengeluaranAccounting || filteredData?.totalPengeluaran || 0)}</div>
                  <div className="col-span-2 text-right text-red-800">{formatRupiah(filteredData?.totalPengeluaranAccounting || filteredData?.totalPengeluaran || 0)}</div>
                </div>
              </div>
            </div>

            {/* SALDO BERSIH - FINAL */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-5 rounded-xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-200 uppercase tracking-wide">III. SALDO BERSIH</p>
                  <p className="text-3xl font-bold mt-1">{formatRupiah(filteredData?.saldoBersihAccounting || (filteredData?.totalPemasukanAccounting || filteredData?.totalPemasukan || 0) - (filteredData?.totalPengeluaranAccounting || filteredData?.totalPengeluaran || 0))}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-blue-200">Per {filteredData?.periodLabel}</p>
                  <div className="mt-2 space-y-1 text-xs text-blue-100">
                    <p>Pemasukan: {formatRupiah(filteredData?.totalPemasukanAccounting || filteredData?.totalPemasukan || 0)}</p>
                    <p>Pengeluaran: -{formatRupiah(filteredData?.totalPengeluaranAccounting || filteredData?.totalPengeluaran || 0)}</p>
                  </div>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-blue-200 mb-1">
                  <span>Persentase Pengeluaran</span>
                  <span>{filteredData?.totalPemasukanAccounting > 0 ? Math.round(((filteredData?.totalPengeluaranAccounting || filteredData?.totalPengeluaran || 0) / (filteredData?.totalPemasukanAccounting || filteredData?.totalPemasukan || 0)) * 100) : 0}%</span>
                </div>
                <div className="w-full bg-blue-900 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-green-400 to-green-500 h-2 rounded-full"
                    style={{ width: `${filteredData?.totalPemasukanAccounting > 0 ? Math.min(((filteredData?.totalPemasukanAccounting || filteredData?.totalPemasukan || 0) - (filteredData?.totalPengeluaranAccounting || filteredData?.totalPengeluaran || 0)) / (filteredData?.totalPemasukanAccounting || filteredData?.totalPemasukan || 0) * 100, 100) : 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Fee Admin & Pengeluaran Details - Based on Period */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Fee Admin Section */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg"><Coins className="text-purple-600" size={24} /></div>
            <h3 className="font-semibold text-gray-900">Detail Fee Admin</h3>
          </div>

          <div className="space-y-4">
            {/* From Kasir Reports */}
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-purple-700">Dari Laporan Kasir</span>
                <span className="text-xs text-purple-600">{filteredData?.cashierFiltered?.length || 0} laporan</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Total Setoran</p>
                  <p className="font-bold text-purple-700">{formatRupiah(filteredData?.totalSetoran || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Fee Admin</p>
                  <p className="font-bold text-purple-700">{formatRupiah(filteredData?.cashierTotalAdmin || 0)}</p>
                </div>
              </div>
            </div>

            {/* From Loket Reports */}
            <div className="p-4 bg-indigo-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-indigo-700">Dari Laporan Loket</span>
                <span className="text-xs text-indigo-600">{filteredData?.loketFiltered?.length || 0} laporan</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Total Omzet</p>
                  <p className="font-bold text-indigo-700">{formatRupiah(filteredData?.totalOmzet || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Fee Admin</p>
                  <p className="font-bold text-indigo-700">{formatRupiah(filteredData?.loketTotalAdmin || 0)}</p>
                </div>
              </div>
            </div>

            {/* Total */}
            <div className="p-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded-lg border border-purple-300">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-purple-800">Total Fee Admin</span>
                <span className="text-xl font-bold text-purple-700">{formatRupiah(filteredData?.totalFeeAdmin || 0)}</span>
              </div>
              <div className="mt-2 text-xs text-purple-600">
                Rata-rata: {formatRupiah(filteredData?.avgPemasukan || 0)}/hari
              </div>
            </div>
          </div>
        </Card>

        {/* Pengeluaran Section - Today's Detail */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg"><TrendingDown className="text-orange-600" size={24} /></div>
            <h3 className="font-semibold text-gray-900">Pengeluaran Hari Ini</h3>
          </div>

          <div className="space-y-3">
            {/* Today's pengeluaran list */}
            {(filteredData?.todayPengeluaran?.length > 0) ? (
              filteredData.todayPengeluaran.map((p, idx) => (
                <div key={p.id || idx} className="p-3 bg-red-50 rounded-lg flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-800">{p.jenis?.replace('_', ' ') || 'Lainnya'}</p>
                    <p className="text-sm text-gray-500">{p.keterangan || '-'}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">-{formatRupiah(p.jumlah)}</p>
                    <StatusBadge status={p.status} />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-400">
                <TrendingDown size={32} className="mx-auto mb-2" />
                <p>Tidak ada pengeluaran hari ini</p>
              </div>
            )}

            {/* Summary */}
            <div className="p-4 bg-gradient-to-r from-red-100 to-red-200 rounded-lg border border-red-300">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-red-800">Total Pengeluaran</span>
                <span className="text-xl font-bold text-red-700">{formatRupiah(filteredData?.todayPengeluaranTotal || 0)}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Riwayat Fee Admin - Combined (Loket + Kasir) with Pagination */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 bg-indigo-100 rounded-lg"><History className="text-indigo-600" size={24} /></div>
          <h3 className="font-semibold text-gray-900">Riwayat Fee Admin ({filteredData?.periodLabel || 'Semua Data'})</h3>
        </div>
        {(() => {
          // Combine laporan_shift history and cashier_history
          const loketHistory = data?.admin_fee_summary?.history || [];
          const kasirHistory = data?.cashier_history || [];

          // Merge and sort by date
          const combinedHistory = [...loketHistory, ...kasirHistory].sort((a, b) =>
            new Date(b.tanggal) - new Date(a.tanggal)
          );

          if (combinedHistory.length === 0) return (
            <div className="text-center py-8">
              <Coins className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Belum ada data fee admin</p>
            </div>
          );

          // Pagination
          const totalPages = Math.ceil(combinedHistory.length / feeAdminPerPage);
          const paginatedHistory = combinedHistory.slice(
            (feeAdminPage - 1) * feeAdminPerPage,
            feeAdminPage * feeAdminPerPage
          );

          return (
            <>
              <div className="space-y-3">
                {paginatedHistory.map((day, idx) => {
                  const isKasir = day.reports?.[0]?.kasir_name !== undefined;
                  return (
                    <div key={day.tanggal} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="w-24">
                            <p className="font-medium text-gray-900">{formatDate(day.tanggal)}</p>
                            <p className="text-xs text-gray-500">{day.jumlah_laporan} laporan</p>
                          </div>
                          <div className="hidden md:block w-px h-10 bg-gray-300"></div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <p className="text-xs text-gray-500">{isKasir ? 'Setoran' : 'Omzet'}</p>
                              <p className="font-semibold">{formatRupiah(day.total_setoran || day.total_omzet || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Fee Admin</p>
                              <p className="font-semibold text-purple-600">{formatRupiah(day.total_admin || day.total_admin_fee || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Transaksi</p>
                              <p className="font-semibold">{day.total_transaksi || 0}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {combinedHistory.length > feeAdminPerPage && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">Halaman {feeAdminPage} / {totalPages}</p>
                  <div className="flex gap-2">
                    <button
                      disabled={feeAdminPage === 1}
                      onClick={() => setFeeAdminPage(p => p - 1)}
                      className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                    >← Prev</button>
                    <button
                      disabled={feeAdminPage >= totalPages}
                      onClick={() => setFeeAdminPage(p => p + 1)}
                      className="px-3 py-1 text-sm border rounded-lg disabled:opacity-40 hover:bg-gray-50"
                    >Next →</button>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Card>

      {/* Verified Today Reports */}
      {data?.verified_today?.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="text-green-600" size={24} /></div>
            <h3 className="font-semibold text-gray-900">Laporan Terverifikasi Hari Ini</h3>
          </div>
          <div className="space-y-2">
            {data.verified_today.map(lap => (
              <div key={lap.id} className="p-3 bg-green-50 rounded-lg flex justify-between items-center">
                <div>
                  <p className="font-medium">{lap.loket_nama}</p>
                  <p className="text-sm text-gray-500">{lap.shift_period} - {lap.agent_nama}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">+{formatRupiah(lap.total_omzet)}</p>
                  <p className="text-xs text-purple-600">Fee: {formatRupiah(lap.total_admin_fee || 0)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Pemasukan List */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Pemasukan dari Loket (Verified)</h3>
        {data?.pemasukan_today?.length > 0 ? (
          <div className="space-y-2">
            {data.pemasukan_today.map(p => (
              <div key={p.id} className="p-3 bg-green-50 rounded-lg flex justify-between items-center">
                <div>
                  <p className="font-medium">{p.loket_nama}</p>
                  <p className="text-sm text-gray-500">{p.keterangan}</p>
                </div>
                <p className="font-semibold text-green-600">+{formatRupiah(p.jumlah)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Belum ada pemasukan verified hari ini</p>
        )}
      </Card>

      {/* Pengeluaran List */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Pengeluaran Hari Ini</h3>
        {data?.pengeluaran_today?.length > 0 ? (
          <div className="space-y-2">
            {data.pengeluaran_today.map(p => (
              <div key={p.id} className="p-3 bg-red-50 rounded-lg flex justify-between items-center">
                <div>
                  <p className="font-medium">{p.jenis.replace('_', ' ')}</p>
                  <p className="text-sm text-gray-500">{p.keterangan}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-red-600">-{formatRupiah(p.jumlah)}</p>
                  <StatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Belum ada pengeluaran hari ini</p>
        )}
      </Card>

      {/* Riwayat Laporan Kasir */}
      {(data?.cashier_reports?.length > 0 || data?.cashier_history?.length > 0) && (
        <Card className="p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-teal-100 rounded-lg"><FileText className="text-teal-600" size={24} /></div>
              <h3 className="font-semibold text-gray-900">Riwayat Laporan Kasir</h3>
            </div>

            {/* Period Selector */}
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                value={kasirPeriod}
                onChange={(e) => setKasirPeriod(e.target.value)}
              >
                <option value="today">Hari Ini</option>
                <option value="7days">7 Hari Terakhir</option>
                <option value="30days">30 Hari Terakhir</option>
                <option value="month">Bulan Ini</option>
                <option value="custom">Custom Range</option>
              </select>

              {kasirPeriod === 'custom' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="date"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Summary Cards - Based on Selected Period */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg text-center border border-teal-200 col-span-2 md:col-span-1">
              <p className="text-xs text-teal-600 mb-1">Periode</p>
              <p className="text-sm font-medium text-teal-800">{filteredData?.periodLabel}</p>
            </div>
            <div className="p-4 bg-teal-50 rounded-lg text-center">
              <p className="text-xl lg:text-2xl font-bold text-teal-600">{formatRupiah(filteredData?.totalSetoran || 0)}</p>
              <p className="text-sm text-gray-500">Total Setoran</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg text-center">
              <p className="text-xl lg:text-2xl font-bold text-purple-600">{formatRupiah(filteredData?.totalAdmin || 0)}</p>
              <p className="text-sm text-gray-500">Total Fee Admin</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-xl lg:text-2xl font-bold text-blue-600">{filteredData?.totalLaporan || 0}</p>
              <p className="text-sm text-gray-500">Jumlah Laporan</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg text-center">
              <p className="text-xl lg:text-2xl font-bold text-orange-600">{formatRupiah(filteredData?.avgSetoran || 0)}</p>
              <p className="text-sm text-gray-500">Rata-rata Setoran</p>
            </div>
            <div className="p-4 bg-pink-50 rounded-lg text-center">
              <p className="text-xl lg:text-2xl font-bold text-pink-600">{formatRupiah(filteredData?.avgAdmin || 0)}</p>
              <p className="text-sm text-gray-500">Rata-rata Fee Admin</p>
            </div>
          </div>

          {/* Cashier History - Based on Selected Period */}
          {filteredData?.filteredHistory?.length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-3">Riwayat {filteredData.periodLabel}</h4>
              <div className="space-y-3">
                {filteredData.filteredHistory.map((day, idx) => (
                  <div key={day.tanggal} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="w-28">
                          <p className="font-medium text-gray-900">{formatDate(day.tanggal)}</p>
                          <p className="text-xs text-gray-500">{day.jumlah_laporan} laporan</p>
                        </div>
                        <div className="hidden md:block w-px h-10 bg-gray-300"></div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Setoran</p>
                            <p className="font-semibold text-teal-600">{formatRupiah(day.total_setoran)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Fee Admin</p>
                            <p className="font-semibold text-purple-600">{formatRupiah(day.total_admin)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Detail per laporan */}
                    {day.reports?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {day.reports.map((report, rIdx) => (
                            <div key={report.id || rIdx} className="p-2 bg-white rounded border border-gray-100 text-sm">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-gray-800 text-xs">{report.kasir_name}</p>
                                  <p className="text-xs text-gray-500">Saldo: {formatRupiah(report.saldo_brangkas || 0)}</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-purple-600 text-xs">{formatRupiah(report.penerimaan_admin || 0)}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cashier Reports Table - Based on Selected Period */}
          {filteredData?.filteredReports?.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Detail Laporan Kasir</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Kasir</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Setoran</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Fee Admin</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Saldo Brangkas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredData.filteredReports.slice(0, 20).map((report, idx) => (
                      <tr key={report.id || idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-900">-</td>
                        <td className="px-3 py-2 font-medium text-gray-900">{report.kasir_name}</td>
                        <td className="px-3 py-2 text-right text-teal-600 font-semibold">{formatRupiah(report.total_setoran || 0)}</td>
                        <td className="px-3 py-2 text-right text-purple-600 font-semibold">{formatRupiah(report.penerimaan_admin || 0)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatRupiah(report.saldo_brangkas || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Empty state if no data for selected period */}
          {filteredData && filteredData.filteredHistory?.length === 0 && (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Tidak ada laporan kasir untuk periode {filteredData.periodLabel}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

// ==================== VERIFIKASI LOKET (KASIR) ====================

const VerifikasiLoketPage = ({ currentUser }) => {
  const [laporanList, setLaporanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLaporan, setSelectedLaporan] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('PENDING'); // PENDING, VERIFIED_KASIR, ALL

  useEffect(() => { loadData(); }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      let url = '/api/laporan-shift';
      if (filter !== 'ALL') {
        url += `?status=${filter}`;
      }
      const result = await api.get(url);
      setLaporanList(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (action, catatan) => {
    try {
      await api.put(`/api/laporan-shift/${selectedLaporan.id}/verify?status=${action}&catatan=${encodeURIComponent(catatan || '')}`);
      setSuccess(`Laporan berhasil di-${action.toLowerCase()}!`);
      setSelectedLaporan(null);
      setShowVerifyModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  // Determine what actions current currentUser can take
  const canVerify = (laporan) => {
    const status = laporan.status;
    if (currentUser?.role === 'KASIR') {
      return status === 'PENDING';
    }
    if (currentUser?.role === 'MANAGER') {
      return status === 'PENDING' || status === 'VERIFIED_KASIR';
    }
    if (currentUser?.role === 'OWNER') {
      return !status.startsWith('VERIFIED_OWNER') && !status.startsWith('REJECTED_OWNER');
    }
    return false;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Verifikasi Laporan Loket</h1>
        <div className="flex items-center gap-2">
          <select 
            className="px-3 py-2 border rounded-lg text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="PENDING">Pending</option>
            <option value="VERIFIED_KASIR">Verified Kasir</option>
            <option value="ALL">Semua</option>
          </select>
          <Button variant="outline" size="sm" onClick={loadData}><RefreshCw size={16} /> Refresh</Button>
        </div>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {laporanList.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <p className="text-gray-500">Tidak ada laporan dengan status "{filter}"</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* List */}
          <Card>
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">Daftar Laporan ({laporanList.length})</h3>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {laporanList.map(lap => (
                <button
                  key={lap.id}
                  onClick={() => setSelectedLaporan(lap)}
                  className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${selectedLaporan?.id === lap.id ? 'bg-primary-50 border-l-4 border-l-primary-600' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{lap.loket_nama}</p>
                      <p className="text-sm text-gray-500">{lap.shift_period} - {lap.agent_nama}</p>
                      <p className="text-xs text-gray-400">{formatDateTime(lap.created_at)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-primary-600">{formatRupiah(lap.total_omzet)}</p>
                      <StatusBadge status={lap.status} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {/* Detail */}
          <Card>
            {selectedLaporan ? (
              <div className="p-5 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedLaporan.loket_nama}</h3>
                    <p className="text-sm text-gray-500">{selectedLaporan.nomor_laporan}</p>
                  </div>
                  <StatusBadge status={selectedLaporan.status} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Shift</p>
                    <p className="font-semibold">{selectedLaporan.shift_period}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Agent</p>
                    <p className="font-semibold">{selectedLaporan.agent_nama}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-gray-700">Detail Transaksi</h4>
                  {selectedLaporan.transaksi_list?.map((t, idx) => (
                    <div key={idx} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                      <span>{t.provider_name || t.nama_provider || 'Unknown'}</span>
                      <span>
                        {t.jumlah_transaksi || t.jumlah_trx || 0} trx - {formatRupiah((t.total_nominal || 0) + (t.total_admin_fee || 0))}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="p-4 bg-primary-50 rounded-lg space-y-2">
                  <div className="flex justify-between"><span>Total Transaksi:</span><span className="font-semibold">{selectedLaporan.total_transaksi}</span></div>
                  <div className="flex justify-between"><span>Tunai:</span><span className="font-semibold">{formatRupiah(selectedLaporan.total_tunai)}</span></div>
                  <div className="flex justify-between"><span>Non-Tunai:</span><span className="font-semibold">{formatRupiah(selectedLaporan.total_non_tunai)}</span></div>
                  <div className="flex justify-between"><span>Uang Kecil:</span><span className="font-semibold">{formatRupiah(selectedLaporan.uang_kecil)}</span></div>
                  <div className="flex justify-between border-t pt-2"><span>Total Omzet:</span><span className="font-semibold text-primary-600">{formatRupiah(selectedLaporan.total_omzet)}</span></div>
                  <div className="flex justify-between"><span>Selisih Kas:</span><span className={`font-semibold ${selectedLaporan.selisih === 0 ? 'text-green-600' : 'text-red-600'}`}>{formatRupiah(selectedLaporan.selisih)}</span></div>
                </div>

                {selectedLaporan.bukti_urls?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Bukti Transaksi</h4>
                    <BuktiGallery urls={selectedLaporan.bukti_urls} />
                  </div>
                )}

                {selectedLaporan.catatan && (
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-gray-500">Catatan dari Loket:</p>
                    <p className="text-sm">{selectedLaporan.catatan}</p>
                  </div>
                )}

                {/* Verification History */}
                {selectedLaporan.verification_history?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-700">Riwayat Verifikasi</h4>
                    {selectedLaporan.verification_history.map((h, idx) => (
                      <div key={idx} className={`p-3 rounded-lg text-sm ${h.action === 'APPROVED' ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex justify-between">
                          <span className="font-medium">{h.by_nama} ({h.by_role})</span>
                          <StatusBadge status={h.status} />
                        </div>
                        {h.catatan && <p className="text-gray-600 mt-1">"{h.catatan}"</p>}
                        <p className="text-xs text-gray-400 mt-1">{formatDateTime(h.timestamp)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {canVerify(selectedLaporan) && (
                  <div className="pt-4">
                    <Button className="w-full" onClick={() => setShowVerifyModal(true)}>
                      <CheckCircle size={18} /> Verifikasi Laporan Ini
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Eye className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p>Pilih laporan untuk melihat detail</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Verify Modal */}
      <VerifyModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        onVerify={handleVerify}
        title="Verifikasi Laporan Shift"
        itemInfo={selectedLaporan && (
          <div>
            <p><strong>{selectedLaporan.loket_nama}</strong> - {selectedLaporan.shift_period}</p>
            <p>Total Omzet: <strong>{formatRupiah(selectedLaporan.total_omzet)}</strong></p>
            <p>Agent: {selectedLaporan.agent_nama}</p>
          </div>
        )}
      />
    </div>
  );
};

// ==================== PENGELUARAN (KASIR) ====================

const PengeluaranPage = ({ currentUser }) => {
  const [pengeluaranList, setPengeluaranList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdPengeluaran, setCreatedPengeluaran] = useState(null);
  
  const [formData, setFormData] = useState({
    jenis: 'DEPOSIT_AGEN',
    jumlah: '',
    keterangan: '',
    penerima: '',
    no_rekening: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.get('/api/pengeluaran');
      setPengeluaranList(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const result = await api.post('/api/pengeluaran', {
        ...formData,
        jumlah: parseFloat(formData.jumlah)
      });
      setCreatedPengeluaran(result);
      setSuccess('Pengeluaran berhasil dicatat! Silakan upload bukti.');
      setShowModal(false);
      setFormData({ jenis: 'DEPOSIT_AGEN', jumlah: '', keterangan: '', penerima: '', no_rekening: '' });
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadBukti = async (file, pengeluaranId) => {
    setUploading(true);
    try {
      await api.uploadFile(`/api/pengeluaran/${pengeluaranId}/upload-bukti`, file);
      setSuccess('Bukti berhasil diupload!');
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setCreatedPengeluaran(null);
    }
  };

  const jenisIcons = {
    DEPOSIT_AGEN: <PiggyBank className="text-orange-600" size={20} />,
    TRANSFER_BANK: <Send className="text-blue-600" size={20} />,
    BIAYA_OPERASIONAL: <Car className="text-gray-600" size={20} />
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Pengeluaran</h1>
        <Button onClick={() => setShowModal(true)}><Plus size={18} /> Tambah Pengeluaran</Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Upload bukti for just created pengeluaran */}
      {createdPengeluaran && (
        <Card className="p-4 border-l-4 border-l-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Upload bukti untuk pengeluaran yang baru dibuat</p>
              <p className="text-sm text-gray-500">{createdPengeluaran.nomor} - {formatRupiah(createdPengeluaran.jumlah)}</p>
            </div>
            <FileUpload onUpload={(file) => handleUploadBukti(file, createdPengeluaran.id)} uploading={uploading} />
          </div>
        </Card>
      )}

      {/* Pengeluaran List */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jenis</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keterangan</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Jumlah</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Bukti</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pengeluaranList.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{formatDate(p.tanggal)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {jenisIcons[p.jenis]}
                      <span className="text-sm">{p.jenis.replace('_', ' ')}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <p>{p.keterangan}</p>
                    {p.penerima && <p className="text-xs text-gray-500">Kepada: {p.penerima}</p>}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">{formatRupiah(p.jumlah)}</td>
                  <td className="px-4 py-3 text-center">
                    {p.bukti_urls?.length > 0 ? (
                      <BuktiGallery urls={p.bukti_urls} />
                    ) : (
                      <FileUpload onUpload={(file) => handleUploadBukti(file, p.id)} uploading={uploading} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Tambah Pengeluaran">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Jenis Pengeluaran"
            value={formData.jenis}
            onChange={(e) => setFormData({ ...formData, jenis: e.target.value })}
            options={[
              { value: 'DEPOSIT_AGEN', label: 'Deposit Agen Reseller (Top Up Saldo PPOB)' },
              { value: 'TRANSFER_BANK', label: 'Transfer ke Bank Kas Perusahaan' },
              { value: 'BIAYA_OPERASIONAL', label: 'Biaya Operasional (Parkir, dll)' }
            ]}
          />
          <Input label="Jumlah (Rp)" type="number" value={formData.jumlah} onChange={(e) => setFormData({ ...formData, jumlah: e.target.value })} placeholder="0" required />
          <Input label="Keterangan" value={formData.keterangan} onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })} placeholder="Keterangan pengeluaran" required />
          
          {formData.jenis === 'DEPOSIT_AGEN' && (
            <Input label="Nama Agen" value={formData.penerima} onChange={(e) => setFormData({ ...formData, penerima: e.target.value })} placeholder="Nama agen reseller" />
          )}
          
          {formData.jenis === 'TRANSFER_BANK' && (
            <>
              <Input label="Bank Tujuan" value={formData.penerima} onChange={(e) => setFormData({ ...formData, penerima: e.target.value })} placeholder="Nama bank" />
              <Input label="No. Rekening" value={formData.no_rekening} onChange={(e) => setFormData({ ...formData, no_rekening: e.target.value })} placeholder="Nomor rekening" />
            </>
          )}
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowModal(false)}>Batal</Button>
            <Button type="submit" className="flex-1" loading={submitting}><Save size={18} /> Simpan</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== DASHBOARD OWNER ====================

const DashboardOwner = ({ currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');

  useEffect(() => { loadData(selectedPeriod); }, []);

  // Auto refresh every 30 seconds
  useEffect(() => {
    if (selectedPeriod === 'today') {
      const interval = setInterval(() => loadData(selectedPeriod), 30000);
      return () => clearInterval(interval);
    }
  }, [selectedPeriod]);

  const loadData = async (period) => {
    setLoading(true);
    try {
      const result = await api.get(`/api/dashboard/owner?period=${period}`);
      setData(result);
      setLastUpdate(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) return <LoadingSpinner size="lg" />;

  const labaRugi = data?.laba_rugi;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard {currentUser?.role === 'OWNER' ? 'Owner' : 'Manager'}</h1>
          <p className="text-sm text-gray-500">
            {data?.latest_date_with_data && data?.latest_date_with_data !== data?.today ? (
              <span className="text-amber-600 font-medium">
                ⚠️ Data terakhir: {data.latest_date_with_data} | Hari ini belum ada data
              </span>
            ) : (
              <>
                Terakhir diperbarui: {lastUpdate ? formatDateTime(lastUpdate) : '-'}
                <span className="ml-2 inline-flex items-center text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                  Real-time
                </span>
              </>
            )}
          </p>
          {/* Date Range Info */}
          {data?.start_date !== data?.end_date && (
            <p className="text-xs text-gray-400 mt-0.5">
              Showing: {data.start_date} - {data.end_date}
            </p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {/* Period Selector */}
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
            value={selectedPeriod}
            onChange={(e) => {
              setSelectedPeriod(e.target.value);
              loadData(e.target.value);
            }}
          >
            <option value="today">Hari Ini</option>
            <option value="7days">7 Hari Terakhir</option>
            <option value="30days">30 Hari Terakhir</option>
            <option value="month">Bulan Ini</option>
            <option value="all">Semua Data</option>
          </select>
          <Button variant="outline" size="sm" onClick={() => loadData(selectedPeriod)} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
          </Button>
        </div>
      </div>

      {/* Owner Pro Tools - Only for OWNER */}
      {currentUser?.role === 'OWNER' && (
        <OwnerProTools
          currentUser={currentUser}
          api={api}
          loadData={loadData}
          loading={loading}
        />
      )}

      {/* Manajemen Saldo Awal - REMOVED: Duplikasi dengan fitur Edit Saldo di OwnerProTools */}
      {/* Manajemen saldo awal sekarang ada di OwnerProTools → Pengaturan Sistem */}
      {/* {currentUser?.role === 'OWNER' && (
        <SaldoAwalManagement
          currentUser={currentUser}
          api={API_URL}
        />
      )} */}
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard title="Total Omzet" value={formatRupiah(data?.summary?.total_omzet || 0)} icon={TrendingUp} color="green" />
        <StatCard title="Total Admin Fee" value={formatRupiah(data?.summary?.total_admin_fee || 0)} icon={DollarSign} color="blue" />
        <StatCard title="Total Transaksi" value={(data?.summary?.total_transaksi || 0).toLocaleString()} icon={Receipt} color="indigo" />
        <StatCard title="Laba Bersih" value={formatRupiah(labaRugi?.laba_bersih || 0)} icon={DollarSign} color="purple" />
        <StatCard title="Loket Aktif" value={data?.loket_count || 0} icon={Store} color="amber" />
      </div>

      {/* Pending Alert */}
      {data?.pending?.total > 0 && (
        <Card className="p-4 border-l-4 border-l-yellow-500">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-yellow-600" size={24} />
            <div>
              <p className="font-medium">Menunggu Verifikasi</p>
              <p className="text-sm text-gray-500">
                {data.pending.laporan > 0 && `${data.pending.laporan} laporan loket`}
                {data.pending.pengeluaran > 0 && ` • ${data.pending.pengeluaran} pengeluaran`}
                {data.pending.akuntansi > 0 && ` • ${data.pending.akuntansi} akuntansi`}
                {data.pending.topup_deposit > 0 && ` • ${data.pending.topup_deposit} request top-up`}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* RINGKASAN LAPORAN SHIFT - Based on Laporan Shift Reports */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">📊 Ringkasan Laporan Shift</h3>
          <span className="text-sm text-gray-500">
            {data?.start_date !== data?.end_date ? `${formatDate(data?.start_date)} - ${formatDate(data?.end_date)}` : formatDate(data?.today)}
          </span>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600 font-medium mb-1">Total Omzet</p>
            <p className="font-bold text-blue-700 text-lg">{formatRupiah(labaRugi?.pendapatan?.total_omzet || 0)}</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-xs text-green-600 font-medium mb-1">Total Admin Fee</p>
            <p className="font-bold text-green-700 text-lg">{formatRupiah(labaRugi?.pendapatan?.total_admin_fee || 0)}</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-600 font-medium mb-1">Total Laporan</p>
            <p className="font-bold text-purple-700 text-lg">{labaRugi?.total_laporan || 0}</p>
          </div>
          <div className="text-center p-3 bg-indigo-50 rounded-lg">
            <p className="text-xs text-indigo-600 font-medium mb-1">Total Transaksi</p>
            <p className="font-bold text-indigo-700 text-lg">{(labaRugi?.total_transaksi || 0).toLocaleString()}</p>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <p className="text-xs text-amber-600 font-medium mb-1">Laba Bersih</p>
            <p className="font-bold text-amber-700 text-lg">{formatRupiah(labaRugi?.laba_bersih || 0)}</p>
          </div>
        </div>

        {/* Tunai vs Non-Tunai */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border rounded-lg p-3 bg-green-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="font-semibold text-green-800">TUNAI</span>
            </div>
            <p className="text-2xl font-bold text-green-700">
              {formatRupiah(labaRugi?.total_tunai || 0)}
            </p>
          </div>
          <div className="border rounded-lg p-3 bg-blue-50">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
              <span className="font-semibold text-blue-800">NON-TUNAI</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">
              {formatRupiah(labaRugi?.total_non_tunai || 0)}
            </p>
          </div>
        </div>
      </Card>

      {/* Deposit Bank Summary */}
      {data?.deposits_summary?.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">💰 Saldo Deposit Bank</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.deposits_summary.map(dep => (
              <div key={dep.provider_id} className="p-4 bg-gray-50 rounded-lg flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">{dep.provider_nama}</p>
                  <p className="text-sm text-gray-500">Saldo Deposit</p>
                </div>
                <p className={`font-bold text-lg ${dep.saldo > 1000000 ? 'text-green-600' : dep.saldo > 500000 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {formatRupiah(dep.saldo)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Loket Performance with Tunai/Non-Tunai breakdown */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">🏪 Performa Loket</h3>
          <span className="text-sm text-gray-500">
            {data?.start_date !== data?.end_date ? `${formatDate(data?.start_date)} - ${formatDate(data?.end_date)}` : formatDate(data?.today)}
          </span>
        </div>
        <div className="space-y-4">
          {data?.laporan_per_loket?.map(loket => (
            <div key={loket.loket_id} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-medium text-gray-900">{loket.loket_nama}</p>
                  <p className="text-sm text-gray-500">{loket.total_transaksi} transaksi</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary-600 text-lg">{formatRupiah(loket.total_omzet)}</p>
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">Tunai: {formatRupiah(loket.total_tunai || 0)}</span>
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Non-Tunai: {formatRupiah(loket.total_non_tunai || 0)}</span>
                  </div>
                </div>
              </div>
              {loket.laporan?.length > 0 && (
                <div className="mt-2 space-y-1 border-t pt-2">
                  {loket.laporan.map(lap => (
                    <div key={lap.id} className="flex justify-between text-sm">
                      <span className="text-gray-500">{lap.shift} - {lap.agent}</span>
                      <div className="flex items-center gap-2">
                        <span>{formatRupiah(lap.omzet)}</span>
                        <StatusBadge status={lap.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {(!data?.laporan_per_loket || data.laporan_per_loket.length === 0) && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">Belum ada laporan loket dalam periode ini</p>
              {data?.latest_date_with_data && data.latest_date_with_data !== data?.today && (
                <p className="text-sm text-amber-600">📅 Data terakhir: {data.latest_date_with_data}</p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* TRANSAKSI PPOB PER PROVIDER - Sinkronisasi dengan Laporan Shift */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">📊 Transaksi PPOB Per Provider</h3>
          <span className="text-sm text-gray-500">
            {data?.start_date !== data?.end_date ? `${formatDate(data?.start_date)} - ${formatDate(data?.end_date)}` : formatDate(data?.today)}
          </span>
        </div>
        <div className="space-y-3">
          {data?.ppob_summary?.map((ppob, idx) => (
            <div key={ppob.provider_id} className="border-l-4 border-indigo-500 bg-indigo-50 rounded-r-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-gray-900">{ppob.provider_nama}</span>
                    <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-0.5 rounded">{ppob.kategori}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Transaksi: </span>
                      <span className="font-semibold ml-1">{ppob.total_trx} trx</span>
                    </div>
                    <div>
                      <span className="text-gray-600">TUNAI: </span>
                      <span className="font-semibold ml-1">{ppob.tunai_trx} trx</span>
                    </div>
                    <div>
                      <span className="text-gray-600">NON-TUNAI: </span>
                      <span className="font-semibold ml-1">{ppob.non_tunai_trx} trx</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Admin: </span>
                      <span className="font-semibold ml-1">{formatRupiah(ppob.total_admin)}</span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-indigo-200">
                    <div className="flex gap-4 text-xs">
                      <div>
                        <span className="text-gray-500">BSI: </span>
                        <span className="font-semibold">{ppob.bank_breakdown.BRIS.trx} trx ({formatRupiah(ppob.bank_breakdown.BRIS.tagihan)})</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Mandiri: </span>
                        <span className="font-semibold">{ppob.bank_breakdown.MANDIRI.trx} trx ({formatRupiah(ppob.bank_breakdown.MANDIRI.tagihan)})</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <div className="text-xs text-gray-500">Total Tagihan</div>
                  <div className="text-2xl font-bold text-indigo-700">
                    {formatRupiah(ppob.total_tagihan)}
                  </div>
                  {idx === 0 && ppob.total_tagihan > 0 && (
                    <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded mt-1 inline-block">🏆 Teratas</span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {(!data?.ppob_summary || data.ppob_summary.length === 0) && (
            <div className="text-center py-8 text-gray-500">
              <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Belum ada transaksi PPOB hari ini</p>
              <p className="text-sm">Data akan muncul setelah penjaga loket menginput laporan shift</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

// ==================== VERIFIKASI (OWNER/MANAGER) ====================

const VerifikasiPage = ({ currentUser }) => {
  const [tab, setTab] = useState('laporan');
  const [filter, setFilter] = useState('PENDING');
  const [laporanList, setLaporanList] = useState([]);
  const [pengeluaranList, setPengeluaranList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyType, setVerifyType] = useState('laporan');

  useEffect(() => { loadData(); }, [tab, filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === 'laporan') {
        let url = '/api/laporan-shift';
        if (filter !== 'ALL') url += `?status=${filter}`;
        const result = await api.get(url);
        setLaporanList(result);
      } else {
        let url = '/api/pengeluaran';
        if (filter !== 'ALL') url += `?status=${filter}`;
        const result = await api.get(url);
        setPengeluaranList(result);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openVerifyModal = (item, type) => {
    setSelectedItem(item);
    setVerifyType(type);
    setShowVerifyModal(true);
  };

  const handleVerify = async (action, catatan) => {
    try {
      const endpoint = verifyType === 'laporan' 
        ? `/api/laporan-shift/${selectedItem.id}/verify`
        : `/api/pengeluaran/${selectedItem.id}/verify`;
      
      await api.put(`${endpoint}?status=${action}&catatan=${encodeURIComponent(catatan || '')}`);
      setSuccess(`Data berhasil di-${action.toLowerCase()}!`);
      setShowVerifyModal(false);
      setSelectedItem(null);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Get filter options based on role
  const getFilterOptions = () => {
    const base = [{ value: 'PENDING', label: 'Pending' }];
    if (currentUser?.role === 'MANAGER' || currentUser?.role === 'OWNER') {
      base.push({ value: 'VERIFIED_KASIR', label: 'Verified Kasir' });
    }
    if (currentUser?.role === 'OWNER') {
      base.push({ value: 'VERIFIED_MANAGER', label: 'Verified Manager' });
    }
    base.push({ value: 'ALL', label: 'Semua' });
    return base;
  };

  // Check if currentUser can verify this item
  const canVerify = (item) => {
    const status = item.status;
    if (currentUser?.role === 'MANAGER') {
      return status === 'PENDING' || status === 'VERIFIED_KASIR';
    }
    if (currentUser?.role === 'OWNER') {
      return !status.includes('OWNER');
    }
    return false;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Verifikasi</h1>
        <div className="flex items-center gap-2">
          <select 
            className="px-3 py-2 border rounded-lg text-sm"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            {getFilterOptions().map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={loadData}><RefreshCw size={16} /></Button>
        </div>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={() => setTab('laporan')} className={`px-4 py-2 rounded-lg font-medium ${tab === 'laporan' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
          Laporan Loket
        </button>
        <button onClick={() => setTab('pengeluaran')} className={`px-4 py-2 rounded-lg font-medium ${tab === 'pengeluaran' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
          Pengeluaran
        </button>
      </div>

      {loading ? <LoadingSpinner /> : (
        tab === 'laporan' ? (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loket</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shift</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agent</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Omzet</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Bukti</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Catatan</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {laporanList.map(lap => (
                    <tr key={lap.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">{lap.loket_nama}</td>
                      <td className="px-4 py-3 text-sm">{lap.shift_period}</td>
                      <td className="px-4 py-3 text-sm">{lap.agent_nama}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">{formatRupiah(lap.total_omzet)}</td>
                      <td className="px-4 py-3 text-center">{lap.bukti_urls?.length > 0 ? <BuktiGallery urls={lap.bukti_urls} /> : '-'}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={lap.status} /></td>
                      <td className="px-4 py-3 text-sm text-center max-w-[150px] truncate" title={lap.last_verification_note}>{lap.last_verification_note || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        {canVerify(lap) && (
                          <Button size="sm" variant="outline" onClick={() => openVerifyModal(lap, 'laporan')}>
                            Verifikasi
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {laporanList.length === 0 && <p className="text-center py-8 text-gray-500">Tidak ada data</p>}
            </div>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jenis</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keterangan</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Jumlah</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Bukti</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Catatan</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pengeluaranList.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{formatDate(p.tanggal)}</td>
                      <td className="px-4 py-3 text-sm">{p.jenis.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm">{p.keterangan}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">{formatRupiah(p.jumlah)}</td>
                      <td className="px-4 py-3 text-center">{p.bukti_urls?.length > 0 ? <BuktiGallery urls={p.bukti_urls} /> : '-'}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3 text-sm text-center max-w-[150px] truncate" title={p.verification_note}>{p.verification_note || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        {canVerify(p) && (
                          <Button size="sm" variant="outline" onClick={() => openVerifyModal(p, 'pengeluaran')}>
                            Verifikasi
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pengeluaranList.length === 0 && <p className="text-center py-8 text-gray-500">Tidak ada data</p>}
            </div>
          </Card>
        )
      )}

      {/* Verify Modal */}
      <VerifyModal
        isOpen={showVerifyModal}
        onClose={() => { setShowVerifyModal(false); setSelectedItem(null); }}
        onVerify={handleVerify}
        title={`Verifikasi ${verifyType === 'laporan' ? 'Laporan Shift' : 'Pengeluaran'}`}
        itemInfo={selectedItem && (
          verifyType === 'laporan' ? (
            <div>
              <p><strong>{selectedItem.loket_nama}</strong> - {selectedItem.shift_period}</p>
              <p>Total Omzet: <strong>{formatRupiah(selectedItem.total_omzet)}</strong></p>
              <p>Agent: {selectedItem.agent_nama}</p>
              <p>Status saat ini: <StatusBadge status={selectedItem.status} /></p>
            </div>
          ) : (
            <div>
              <p><strong>{selectedItem.jenis?.replace('_', ' ')}</strong></p>
              <p>Jumlah: <strong className="text-red-600">{formatRupiah(selectedItem.jumlah)}</strong></p>
              <p>Keterangan: {selectedItem.keterangan}</p>
              <p>Status saat ini: <StatusBadge status={selectedItem.status} /></p>
            </div>
          )
        )}
      />
    </div>
  );
};

// ==================== SIMPLE PAGES ====================

const LoketPage = ({ currentUser }) => {
  const [lokets, setLokets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/lokets').then(setLokets).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Manajemen Loket</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {lokets.map(loket => (
          <Card key={loket.id} className="p-5" hover>
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 bg-primary-100 rounded-lg"><Store className="w-6 h-6 text-primary-600" /></div>
              <StatusBadge status={loket.status} />
            </div>
            <h3 className="font-semibold text-gray-900">{loket.nama_loket}</h3>
            <p className="text-sm text-gray-500">{loket.kode_loket}</p>
            <p className="text-sm text-gray-500">{loket.alamat}</p>
          </Card>
        ))}
      </div>
    </div>
  );
};

const ProviderPage = ({ currentUser }) => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    kode_provider: '',
    nama_provider: '',
    kategori: 'LISTRIK',
    biaya_admin: '',
    allow_tunai: true,
    allow_non_tunai: true,
    show_tab: false
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/providers');
      setProviders(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      kode_provider: '',
      nama_provider: '',
      kategori: 'LISTRIK',
      biaya_admin: '',
      allow_tunai: true,
      allow_non_tunai: true,
      show_tab: false
    });
    setSelectedProvider(null);
  };

  const handleOpenCreate = () => { resetForm(); setShowModal(true); };

  const handleOpenEdit = (p) => {
    setSelectedProvider(p);
    setFormData({
      kode_provider: p.kode_provider,
      nama_provider: p.nama_provider,
      kategori: p.kategori,
      biaya_admin: p.biaya_admin,
      allow_tunai: p.allow_tunai !== false,
      allow_non_tunai: p.allow_non_tunai !== false,
      show_tab: p.show_tab === true
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        biaya_admin: parseFloat(formData.biaya_admin) || 0
      };

      if (selectedProvider) {
        await api.put(`/api/providers/${selectedProvider.id}`, {
          nama_provider: payload.nama_provider,
          kategori: payload.kategori,
          biaya_admin: payload.biaya_admin,
          allow_tunai: payload.allow_tunai,
          allow_non_tunai: payload.allow_non_tunai,
          show_tab: payload.show_tab
        });
        setSuccess('Provider berhasil diupdate!');
      } else {
        await api.post('/api/providers', payload);
        setSuccess('Provider berhasil ditambahkan!');
      }
      setShowModal(false);
      resetForm();
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (p) => {
    try {
      const newStatus = !p.is_active;
      await api.put(`/api/providers/${p.id}`, { is_active: newStatus });
      setSuccess(`Provider ${p.nama_provider} berhasil ${newStatus ? 'diaktifkan' : 'dinonaktifkan'}!`);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleTab = async (p) => {
    try {
      await api.put(`/api/providers/${p.id}`, { show_tab: !p.show_tab });
      setSuccess(`Provider ${p.nama_provider} ${!p.show_tab ? 'ditampilkan' : 'disembunyikan'} sebagai tab`);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const kategoriOptions = [
    { value: 'LISTRIK', label: 'Listrik (PLN)' },
    { value: 'BPJS', label: 'BPJS' },
    { value: 'AIR', label: 'Air (PDAM)' },
    { value: 'TV_INTERNET', label: 'TV & Internet' },
    { value: 'TELCO', label: 'Telekomunikasi' },
    { value: 'HEALTH', label: 'Kesehatan' },
    { value: 'BANK', label: 'Bank / Transfer' },
    { value: 'OTHER', label: 'Lainnya' }
  ];

  if (loading) return <LoadingSpinner size="lg" />;

  const canEdit = currentUser?.role === 'OWNER' || currentUser?.role === 'MANAGER';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Provider PPOB</h1>
          <p className="text-sm text-gray-500">
            Total: {providers.length} provider
          </p>
        </div>
        {canEdit && <Button onClick={handleOpenCreate}><Plus size={18} /> Tambah Provider</Button>}
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Info Card */}
      <Card className="p-4 bg-blue-50 border-l-4 border-blue-500">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 mt-0.5" size={20} />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Status Provider</p>
            <ul className="space-y-1 text-blue-700">
              <li>✅ <strong>AKTIF</strong> - Provider muncul di menu Laporan Shift Penjaga Loket dan bisa digunakan untuk transaksi</li>
              <li>❌ <strong>NON-AKTIF</strong> - Provider disembunyikan dari menu Laporan Shift dan tidak bisa digunakan</li>
              <li>🔄 Klik pada badge Status untuk mengaktifkan/menonaktifkan provider</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kode</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama Provider</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Admin Fee</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Metode</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tab?</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                {canEdit && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {providers.map(p => (
                <tr key={p.id} className={`hover:bg-gray-50 ${p.is_active === false ? 'opacity-50 bg-gray-100' : ''}`}>
                  <td className="px-4 py-3 text-sm font-medium">{p.kode_provider}</td>
                  <td className="px-4 py-3 text-sm">
                    {p.nama_provider}
                    {p.is_active === false && <span className="ml-2 text-xs text-red-600">(Non-aktif)</span>}
                  </td>
                  <td className="px-4 py-3 text-sm">{kategoriOptions.find(k => k.value === p.kategori)?.label || p.kategori}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium">{formatRupiah(p.biaya_admin)}</td>
                  <td className="px-4 py-3 text-center text-sm">
                    <div className="flex justify-center gap-1">
                      {p.allow_tunai !== false ? (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">TUNAI</span>
                      ) : null}
                      {p.allow_non_tunai !== false ? (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 border border-purple-200">NON-TUNAI</span>
                      ) : null}
                      {p.allow_tunai === false && p.allow_non_tunai === false ? (
                        <span className="text-gray-400 text-xs italic">None</span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {canEdit ? (
                      <button
                        onClick={() => handleToggleTab(p)}
                        className={`px-3 py-1 rounded-full text-xs font-bold transition-all border ${
                          p.show_tab === true 
                            ? 'bg-blue-600 text-white border-blue-700 shadow-sm' 
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                        }`}
                        title="Klik untuk ubah tab khusus"
                      >
                        {p.show_tab === true ? 'YA' : 'TIDAK'}
                      </button>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        p.show_tab === true ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {p.show_tab === true ? 'YA' : 'TIDAK'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {canEdit ? (
                      <button
                        onClick={() => handleToggleActive(p)}
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        title={`Klik untuk ${p.is_active !== false ? 'non-aktifkan' : 'aktifkan'} provider`}
                      >
                        <StatusBadge status={p.is_active !== false ? 'AKTIF' : 'NONAKTIF'} />
                      </button>
                    ) : (
                      <StatusBadge status={p.is_active !== false ? 'AKTIF' : 'NONAKTIF'} />
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => handleOpenEdit(p)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                        <Edit size={16} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={selectedProvider ? 'Edit Provider' : 'Tambah Provider'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!selectedProvider && (
            <Input label="Kode Provider" value={formData.kode_provider} onChange={(e) => setFormData({ ...formData, kode_provider: e.target.value.toUpperCase() })} placeholder="Contoh: PLN_POSTPAID" required />
          )}
          <Input label="Nama Provider" value={formData.nama_provider} onChange={(e) => setFormData({ ...formData, nama_provider: e.target.value })} placeholder="Nama layanan" required />
          <Select label="Kategori" value={formData.kategori} onChange={(e) => setFormData({ ...formData, kategori: e.target.value })} options={kategoriOptions} />
          <Input label="Biaya Admin (Rp)" type="number" value={formData.biaya_admin} onChange={(e) => setFormData({ ...formData, biaya_admin: e.target.value })} placeholder="2500" required />

          <div className="space-y-2 py-1">
            <label className="block text-sm font-medium text-gray-700">Metode Transaksi yang Diizinkan</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.allow_tunai}
                  onChange={(e) => setFormData({ ...formData, allow_tunai: e.target.checked })}
                  className="rounded text-primary-600 focus:ring-primary-500 border-gray-300 h-4 w-4"
                />
                Tunai (Cash)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.allow_non_tunai}
                  onChange={(e) => setFormData({ ...formData, allow_non_tunai: e.target.checked })}
                  className="rounded text-primary-600 focus:ring-primary-500 border-gray-300 h-4 w-4"
                />
                Non-Tunai
              </label>
            </div>
          </div>

          <div className="py-1">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.show_tab}
                onChange={(e) => setFormData({ ...formData, show_tab: e.target.checked })}
                className="rounded text-primary-600 focus:ring-primary-500 border-gray-300 h-4 w-4"
              />
              Tampilkan sebagai Tab Khusus di Loket
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => { setShowModal(false); resetForm(); }}>Batal</Button>
            <Button type="submit" className="flex-1" loading={submitting}><Save size={18} /> {selectedProvider ? 'Update' : 'Simpan'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

const UserPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [lokets, setLokets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nama_lengkap: '',
    role: 'PENJAGA_LOKET',
    loket_id: '',
    email: '',
    no_hp: '',
    is_active: true,
    kategori_teknisi: ''
  });
  
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, loketsData] = await Promise.all([
        api.get('/api/users'),
        api.get('/api/lokets/all').catch(() => [])
      ]);
      setUsers(usersData);
      setLokets(loketsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      nama_lengkap: '',
      role: 'PENJAGA_LOKET',
      loket_id: '',
      email: '',
      no_hp: '',
      is_active: true,
      kategori_teknisi: ''
    });
    setSelectedUser(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (u) => {
    setSelectedUser(u);
    setFormData({
      username: u.username,
      password: '',
      nama_lengkap: u.nama_lengkap,
      role: u.role,
      loket_id: u.loket_id || '',
      email: u.email || '',
      no_hp: u.no_hp || '',
      is_active: u.is_active,
      kategori_teknisi: u.kategori_teknisi || ''
    });
    setShowModal(true);
  };

  const handleOpenPassword = (u) => {
    setSelectedUser(u);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      if (selectedUser) {
        // Update existing currentUser
        await api.put(`/api/users/${selectedUser.id}`, {
          nama_lengkap: formData.nama_lengkap,
          role: formData.role,
          loket_id: formData.loket_id || null,
          email: formData.email || null,
          no_hp: formData.no_hp || null,
          is_active: formData.is_active,
          kategori_teknisi: formData.role === 'TECHNICIAN' ? (formData.kategori_teknisi || null) : null
        });
        setSuccess('User berhasil diupdate!');
      } else {
        // Create new currentUser
        if (!formData.password || formData.password.length < 6) {
          throw new Error('Password minimal 6 karakter');
        }
        await api.post('/api/users', {
          username: formData.username,
          password: formData.password,
          nama_lengkap: formData.nama_lengkap,
          role: formData.role,
          loket_id: formData.loket_id || null,
          email: formData.email || null,
          no_hp: formData.no_hp || null,
          kategori_teknisi: formData.role === 'TECHNICIAN' ? (formData.kategori_teknisi || null) : null
        });
        setSuccess('User berhasil ditambahkan!');
      }
      setShowModal(false);
      resetForm();
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      setError('Password minimal 6 karakter');
      return;
    }
    setSubmitting(true);
    setError('');
    
    try {
      await api.put(`/api/users/${selectedUser.id}/password?new_password=${encodeURIComponent(newPassword)}`);
      setSuccess('Password berhasil diupdate!');
      setShowPasswordModal(false);
      setNewPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus user ini?')) return;

    try {
      await api.delete(`/api/users/${userId}`);
      setSuccess('User berhasil dihapus!');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/api/users/${u.id}`, { is_active: !u.is_active });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const verifyPasswords = async () => {
    const testPassword = prompt('Masukkan password untuk verifikasi:');
    if (!testPassword) return;
    setSubmitting(true);
    try {
      const results = await api.post('/api/users-verify-passwords', { test_password: testPassword });
      const updatedUsers = users.map(user => {
        const verification = results.find(r => r.id === user.id);
        return {
          ...user,
          password_valid: verification ? verification.password_valid : false
        };
      });
      setUsers(updatedUsers);
      setSuccess('Password berhasil diverifikasi!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Gagal memverifikasi password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  // Group users by role
  const groupedUsers = users.reduce((acc, user) => {
    const role = user.role || 'LAINNYA';
    if (!acc[role]) acc[role] = [];
    acc[role].push(user);
    return acc;
  }, {});

  // Define role order and labels
  const roleOrder = ['OWNER', 'MANAGER', 'SURVEYOR', 'INSTALLER', 'SLO', 'SUPERKASIR', 'KASIR', 'PENJAGA_LOKET', 'TECHNICIAN', 'IT'];

  // Hide OWNER role from MANAGER (Owner is above Manager in hierarchy)
  const isAdminOrOwner = currentUser?.role === 'OWNER' || currentUser?.role === 'IT';
  const roleLabels = {
    'OWNER': 'Owner / Pemilik',
    'MANAGER': 'Manager',
    'SURVEYOR': 'Surveyor',
    'INSTALLER': 'Installer',
    'SLO': 'Petugas SLO',
    'SUPERKASIR': 'Superkasir',
    'KASIR': 'Kasir',
    'PENJAGA_LOKET': 'Penjaga Loket',
    'TECHNICIAN': 'Teknisi',
    'IT': 'IT Support',
    'LAINNYA': 'Lainnya'
  };
  const kategoriLabels = {
    'SURVEYOR_INSTALL': 'Surveyor Instalasi',
    'INSTALLER': 'Installer',
    'SURVEYOR_SLO': 'Surveyor SLO'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Manajemen User</h1>
        {isAdminOrOwner && <Button onClick={handleOpenCreate}><Plus size={18} /> Tambah User</Button>}
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Users grouped by role */}
      {roleOrder.map(role => {
        const roleUsers = groupedUsers[role];
        if (!roleUsers || roleUsers.length === 0) return null;

        // Hide OWNER role from MANAGER
        if (role === 'OWNER' && !isAdminOrOwner) return null;

        return (
          <Card key={role}>
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
              <Users className="text-indigo-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">{roleLabels[role] || role}</h2>
              <span className="ml-auto px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
                {roleUsers.length} user{roleUsers.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama Lengkap</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loket</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      <button
                        onClick={verifyPasswords}
                        disabled={submitting}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        title="Klik untuk verifikasi password"
                      >
                        <Eye size={14} />
                        Status Password
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {roleUsers.map(u => {
                    const loket = lokets.find(l => l.id === u.loket_id);
                    return (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{u.username}</td>
                        <td className="px-4 py-3 text-sm">{u.nama_lengkap}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{loket?.nama_loket || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{u.role === 'TECHNICIAN' ? (kategoriLabels[u.kategori_teknisi] || '-') : '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleToggleActive(u)} className="cursor-pointer">
                            <StatusBadge status={u.is_active ? 'AKTIF' : 'NONAKTIF'} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {u.password_valid === true ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <CheckCircle size={12} /> Aktif
                            </span>
                          ) : u.password_valid === false ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              <X size={12} /> Tidak Aktif
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Belum Dicek</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex gap-1 justify-center">
                            {/* Owner role users can only be edited by Owner (not Manager) */}
                            {(role !== 'OWNER' || isAdminOrOwner) && (
                              <>
                                <button onClick={() => handleOpenEdit(u)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Edit size={16} /></button>
                                <button onClick={() => handleOpenPassword(u)} className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded" title="Ubah Password"><CreditCard size={16} /></button>
                                {isAdminOrOwner && u.id !== currentUser?.id && (
                                  <button onClick={() => handleDelete(u.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Hapus"><Trash2 size={16} /></button>
                                )}
                              </>
                            )}
                            {/* Show read-only for OWNER role users viewed by non-owner */}
                            {role === 'OWNER' && !isAdminOrOwner && (
                              <span className="text-xs text-gray-400 italic">Protected</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}

      {/* Users without defined role */}
      {groupedUsers['LAINNYA'] && groupedUsers['LAINNYA'].length > 0 && (
        <Card>
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
            <Users className="text-gray-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-900">Lainnya</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-2 text-left font-medium text-gray-600">No</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Nama</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Username</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-600">Email</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-600">Status</th>
                  <th className="px-4 py-2 text-center font-medium text-gray-600">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groupedUsers['LAINNYA'].map((u, index) => (
                  <tr key={u.id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium">{u.nama_lengkap || '-'}</td>
                    <td className="px-4 py-3 text-sm font-mono">{u.username || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{u.email || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={u.is_active ? 'AKTIF' : 'NONAKTIF'} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => handleOpenEdit(u)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Edit size={16} /></button>
                        <button onClick={() => handleOpenPassword(u)} className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded" title="Ubah Password"><CreditCard size={16} /></button>
                        {isAdminOrOwner && u.id !== currentUser?.id && (
                          <button onClick={() => handleDelete(u.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Hapus"><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Add/Edit User Modal */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={selectedUser ? 'Edit User' : 'Tambah User'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!selectedUser && (
            <Input label="Username" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="Username untuk login" required />
          )}
          {!selectedUser && (
            <Input label="Password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Minimal 6 karakter" required />
          )}
          <Input label="Nama Lengkap" value={formData.nama_lengkap} onChange={(e) => setFormData({ ...formData, nama_lengkap: e.target.value })} placeholder="Nama lengkap user" required />
          <Select
            label="Role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            options={[
              ...(isAdminOrOwner ? [{ value: 'OWNER', label: 'Owner / Pemilik' }] : []),
              { value: 'MANAGER', label: 'Manager' },
              { value: 'SURVEYOR', label: 'Surveyor' },
              { value: 'INSTALLER', label: 'Installer' },
              { value: 'SLO', label: 'Petugas SLO' },
              { value: 'SUPERKASIR', label: 'Superkasir' },
              { value: 'KASIR', label: 'Kasir' },
              { value: 'PENJAGA_LOKET', label: 'Penjaga Loket' },
              { value: 'TECHNICIAN', label: 'Teknisi' },
              { value: 'IT', label: 'IT Support' }
            ]}
          />
          {(formData.role === 'PENJAGA_LOKET' || formData.role === 'MANAGER') && (
            <Select
              label="Loket"
              value={formData.loket_id}
              onChange={(e) => setFormData({ ...formData, loket_id: e.target.value })}
              options={[{ value: '', label: '-- Pilih Loket --' }, ...lokets.map(l => ({ value: l.id, label: l.nama_loket }))]}
            />
          )}
          {formData.role === 'TECHNICIAN' && (
            <Select
              label="Kategori Teknisi"
              value={formData.kategori_teknisi}
              onChange={(e) => setFormData({ ...formData, kategori_teknisi: e.target.value })}
              options={[
                { value: '', label: '-- Pilih Kategori --' },
                { value: 'SURVEYOR_INSTALL', label: 'Surveyor Instalasi (Survey Instalasi)' },
                { value: 'INSTALLER', label: 'Installer (Instalasi Listrik)' },
                { value: 'SURVEYOR_SLO', label: 'Surveyor SLO (Survey SLO)' }
              ]}
            />
          )}
          <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Email (opsional)" />
          <Input label="No. HP" value={formData.no_hp} onChange={(e) => setFormData({ ...formData, no_hp: e.target.value })} placeholder="Nomor HP (opsional)" />
          {selectedUser && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
              <label htmlFor="is_active" className="text-sm">User Aktif</label>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => { setShowModal(false); resetForm(); }}>Batal</Button>
            <Button type="submit" className="flex-1" loading={submitting}><Save size={18} /> {selectedUser ? 'Update' : 'Simpan'}</Button>
          </div>
        </form>
      </Modal>

      {/* Change Password Modal */}
      <Modal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} title={`Ubah Password - ${selectedUser?.nama_lengkap}`}>
        <form onSubmit={handleUpdatePassword} className="space-y-4">
          <Input label="Password Baru" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimal 6 karakter" required />
          <p className="text-sm text-gray-500">Password akan langsung berubah setelah disimpan.</p>
          
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowPasswordModal(false)}>Batal</Button>
            <Button type="submit" className="flex-1" loading={submitting}><Save size={18} /> Simpan Password</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== USER CREDENTIALS PAGE ====================
const UserCredentialsPage = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Only OWNER (and IT with full access) can manage user credentials
  const isAdminOrOwner = currentUser?.role === 'OWNER' || currentUser?.role === 'IT';

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get('/api/users');
      // Filter: MANAGER tidak boleh melihat OWNER
      const filtered = isAdminOrOwner ? data : data.filter(u => u.role !== 'OWNER');
      setUsers(filtered);
    } catch (err) {
      setError('Gagal memuat data users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Group users by role
  const groupedUsers = users.reduce((acc, user) => {
    const role = user.role || 'LAINNYA';
    if (!acc[role]) acc[role] = [];
    acc[role].push(user);
    return acc;
  }, {});

  const roleOrder = ['OWNER', 'MANAGER', 'SUPERKASIR', 'KASIR', 'PENJAGA_LOKET', 'IT', 'TECHNICIAN'];
  const roleLabels = {
    'OWNER': 'Owner / Pemilik',
    'MANAGER': 'Manager',
    'SUPERKASIR': 'Superkasir',
    'KASIR': 'Kasir',
    'PENJAGA_LOKET': 'Penjaga Loket',
    'TECHNICIAN': 'Teknisi',
    'IT': 'IT Support',
    'LAINNYA': 'Lainnya'
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Kelola User & Password</h1>
        <button onClick={loadUsers} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
          <RefreshCw size={18} /> Refresh
        </button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="text-blue-600 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-blue-900">Kelola User & Password</h3>
            <p className="text-sm text-blue-700 mt-1">
              {isAdminOrOwner
                ? 'Halaman ini menampilkan daftar users dan status aktif/nonaktif. OWNER hanya dapat mengubah password user lain, bukan password akun OWNER itu sendiri.'
                : 'Halaman ini menampilkan daftar users dan status aktif/nonaktif. <strong>User OWNER tidak dapat diedit atau dihapus</strong> karena merupakan role tertinggi di sistem. Untuk mengelola user OWNER, gunakan menu <strong>Manajemen User</strong>.'}
            </p>
          </div>
        </div>
      </div>

      {/* User Credentials by Role */}
      {roleOrder.map(role => {
        const roleUsers = groupedUsers[role];
        if (!roleUsers || roleUsers.length === 0) return null;

        // Hide OWNER rows from non-owner
        if (role === 'OWNER' && !isAdminOrOwner) return null;

        return (
          <Card key={role}>
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
              <Users className="text-indigo-600" size={20} />
              <h2 className="text-lg font-semibold text-gray-900">{roleLabels[role] || role}</h2>
              <span className="ml-auto px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
                {roleUsers.length} user
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">No</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nama Lengkap</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {roleUsers.map((u, index) => (
                    <tr key={u.id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium">{u.nama_lengkap || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{u.username}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{u.email || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <StatusBadge status={u.is_active ? 'AKTIF' : 'NONAKTIF'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })}

      {/* Summary */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="text-gray-600" size={20} />
            <span className="font-medium text-gray-700">Total User Aktif</span>
          </div>
          <span className="text-2xl font-bold text-indigo-600">{users.filter(u => u.is_active).length}</span>
        </div>
      </Card>
    </div>
  );
};

const RiwayatPage = ({ currentUser }) => {
  const [pengeluaran, setPengeluaran] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/pengeluaran').then(setPengeluaran).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Riwayat Transaksi</h1>
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jenis</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keterangan</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Jumlah</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pengeluaran.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{formatDate(p.tanggal)}</td>
                  <td className="px-4 py-3 text-sm">{p.jenis.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-sm">{p.keterangan}</td>
                  <td className="px-4 py-3 text-sm text-right font-semibold">{formatRupiah(p.jumlah)}</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const LaporanKeuanganPage = ({ currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    mulai: new Date().toISOString().split('T')[0].slice(0, 7) + '-01',
    akhir: new Date().toISOString().split('T')[0]
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await api.get(`/api/laporan/keuangan?tanggal_mulai=${dateRange.mulai}&tanggal_akhir=${dateRange.akhir}`);
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Laporan Keuangan</h1>
      
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <Input label="Tanggal Mulai" type="date" value={dateRange.mulai} onChange={(e) => setDateRange({...dateRange, mulai: e.target.value})} className="w-40" />
          <Input label="Tanggal Akhir" type="date" value={dateRange.akhir} onChange={(e) => setDateRange({...dateRange, akhir: e.target.value})} className="w-40" />
          <Button onClick={loadData} loading={loading}><Search size={18} /> Tampilkan</Button>
        </div>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Total Pemasukan" value={formatRupiah(data.total_pemasukan)} icon={TrendingUp} color="green" />
            <StatCard title="Total Pengeluaran" value={formatRupiah(data.total_pengeluaran)} icon={TrendingDown} color="red" />
            <StatCard title="Laba Bersih" value={formatRupiah(data.laba_bersih)} icon={DollarSign} color="blue" />
          </div>

          <Card className="p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Ringkasan Harian</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pemasukan</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pengeluaran</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.daily_summary?.map(d => (
                    <tr key={d.tanggal} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{formatDate(d.tanggal)}</td>
                      <td className="px-4 py-3 text-sm text-right text-green-600">{formatRupiah(d.pemasukan)}</td>
                      <td className="px-4 py-3 text-sm text-right text-red-600">{formatRupiah(d.pengeluaran)}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">{formatRupiah(d.saldo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {!data && !loading && (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Pilih periode dan klik "Tampilkan" untuk melihat laporan</p>
        </Card>
      )}
    </div>
  );
};

// ==================== CSR & LOYALTY PROGRAM ====================

const ProgramCSRPage = ({ currentUser }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    nama_program: '',
    jenis: 'CSR',
    deskripsi: '',
    target: '',
    tanggal_mulai: new Date().toISOString().split('T')[0],
    tanggal_selesai: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/programs');
      setPrograms(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ nama_program: '', jenis: 'CSR', deskripsi: '', target: '', tanggal_mulai: new Date().toISOString().split('T')[0], tanggal_selesai: '' });
    setSelectedProgram(null);
  };

  const handleOpenCreate = () => { resetForm(); setShowModal(true); };

  const handleOpenEdit = (p) => {
    setSelectedProgram(p);
    setFormData({
      nama_program: p.nama_program,
      jenis: p.jenis,
      deskripsi: p.deskripsi,
      target: p.target,
      tanggal_mulai: p.tanggal_mulai,
      tanggal_selesai: p.tanggal_selesai || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const payload = { ...formData, target: parseFloat(formData.target) || 0 };
      
      if (selectedProgram) {
        await api.put(`/api/programs/${selectedProgram.id}`, payload);
        setSuccess('Program berhasil diupdate!');
      } else {
        await api.post('/api/programs', payload);
        setSuccess('Program berhasil ditambahkan!');
      }
      setShowModal(false);
      resetForm();
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateProgress = async (program, newProgress) => {
    try {
      await api.put(`/api/programs/${program.id}`, { progress: parseFloat(newProgress) });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus program ini?')) return;
    try {
      await api.delete(`/api/programs/${id}`);
      setSuccess('Program berhasil dihapus!');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  const isOwner = currentUser?.role === 'OWNER';
  const canUpdateProgress = currentUser?.role === 'MANAGER' || isOwner;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Program CSR & Loyalty</h1>
        {isOwner && <Button onClick={handleOpenCreate}><Plus size={18} /> Buat Program</Button>}
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {programs.length === 0 ? (
        <Card className="p-8 text-center">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Belum ada program CSR atau Loyalty</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {programs.map(p => {
            const progressPercent = p.target > 0 ? Math.min((p.progress / p.target) * 100, 100) : 0;
            return (
              <Card key={p.id} className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className={`text-xs px-2 py-1 rounded-full ${p.jenis === 'CSR' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>{p.jenis}</span>
                    <h3 className="font-semibold text-gray-900 mt-2">{p.nama_program}</h3>
                  </div>
                  <StatusBadge status={p.status} />
                </div>
                <p className="text-sm text-gray-600 mb-3">{p.deskripsi}</p>
                
                {p.target > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Progress</span>
                      <span className="font-medium">{formatRupiah(p.progress)} / {formatRupiah(p.target)}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-primary-600 h-2 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
                    </div>
                  </div>
                )}
                
                <div className="text-xs text-gray-500 mb-3">
                  Mulai: {formatDate(p.tanggal_mulai)} {p.tanggal_selesai && `• Selesai: ${formatDate(p.tanggal_selesai)}`}
                </div>

                {canUpdateProgress && p.target > 0 && (
                  <div className="flex gap-2 items-center mb-3">
                    <Input type="number" placeholder="Update progress" className="flex-1" onBlur={(e) => e.target.value && handleUpdateProgress(p, e.target.value)} />
                  </div>
                )}

                {isOwner && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button variant="outline" size="sm" onClick={() => handleOpenEdit(p)}><Edit size={14} /> Edit</Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(p.id)}><Trash2 size={14} /> Hapus</Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title={selectedProgram ? 'Edit Program' : 'Buat Program'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Nama Program" value={formData.nama_program} onChange={(e) => setFormData({...formData, nama_program: e.target.value})} required />
          <Select label="Jenis" value={formData.jenis} onChange={(e) => setFormData({...formData, jenis: e.target.value})} options={[{value: 'CSR', label: 'CSR (Corporate Social Responsibility)'}, {value: 'LOYALTY', label: 'Loyalty Program'}]} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deskripsi</label>
            <textarea className="w-full px-3 py-2 border rounded-lg" rows={3} value={formData.deskripsi} onChange={(e) => setFormData({...formData, deskripsi: e.target.value})} required />
          </div>
          <Input label="Target (Rp)" type="number" value={formData.target} onChange={(e) => setFormData({...formData, target: e.target.value})} placeholder="Target dana/poin (opsional)" />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Tanggal Mulai" type="date" value={formData.tanggal_mulai} onChange={(e) => setFormData({...formData, tanggal_mulai: e.target.value})} required />
            <Input label="Tanggal Selesai" type="date" value={formData.tanggal_selesai} onChange={(e) => setFormData({...formData, tanggal_selesai: e.target.value})} />
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowModal(false)}>Batal</Button>
            <Button type="submit" className="flex-1" loading={submitting}><Save size={18} /> {selectedProgram ? 'Update' : 'Simpan'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== AKUNTANSI (MANAGER) ====================

const AkuntansiPage = ({ currentUser }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdEntry, setCreatedEntry] = useState(null);
  
  const [formData, setFormData] = useState({
    jenis: 'BIAYA_GAJI',
    jumlah: '',
    keterangan: '',
    tanggal: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/akuntansi');
      setEntries(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const result = await api.post('/api/akuntansi', { ...formData, jumlah: parseFloat(formData.jumlah) });
      setCreatedEntry(result);
      setSuccess('Entry berhasil ditambahkan! Silakan upload bukti.');
      setShowModal(false);
      setFormData({ jenis: 'BIAYA_GAJI', jumlah: '', keterangan: '', tanggal: new Date().toISOString().split('T')[0] });
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadBukti = async (file, entryId) => {
    setUploading(true);
    try {
      await api.uploadFile(`/api/akuntansi/${entryId}/upload-bukti`, file);
      setSuccess('Bukti berhasil diupload!');
      setCreatedEntry(null);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleVerify = async (id, status) => {
    try {
      await api.put(`/api/akuntansi/${id}/verify?status=${status}`);
      setSuccess(`Entry berhasil ${status.toLowerCase()}!`);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const jenisOptions = [
    { value: 'PENDAPATAN_LAIN', label: 'Pendapatan Lain-lain' },
    { value: 'BIAYA_GAJI', label: 'Biaya Gaji' },
    { value: 'BIAYA_OPERASIONAL', label: 'Biaya Operasional' },
    { value: 'BIAYA_LAIN', label: 'Biaya Lain-lain' }
  ];

  const getJenisIcon = (jenis) => {
    if (jenis.startsWith('PENDAPATAN')) return <TrendingUp className="text-green-600" size={18} />;
    return <TrendingDown className="text-red-600" size={18} />;
  };

  if (loading) return <LoadingSpinner size="lg" />;

  const isOwner = currentUser?.role === 'OWNER';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Akuntansi</h1>
        <Button onClick={() => setShowModal(true)}><Plus size={18} /> Tambah Entry</Button>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {createdEntry && (
        <Card className="p-4 border-l-4 border-l-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Upload bukti untuk entry yang baru dibuat</p>
              <p className="text-sm text-gray-500">{jenisOptions.find(j => j.value === createdEntry.jenis)?.label} - {formatRupiah(createdEntry.jumlah)}</p>
            </div>
            <FileUpload onUpload={(file) => handleUploadBukti(file, createdEntry.id)} uploading={uploading} />
          </div>
        </Card>
      )}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Jenis</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keterangan</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Jumlah</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Bukti</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                {isOwner && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {entries.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{formatDate(e.tanggal)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getJenisIcon(e.jenis)}
                      <span className="text-sm">{jenisOptions.find(j => j.value === e.jenis)?.label || e.jenis}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">{e.keterangan}</td>
                  <td className={`px-4 py-3 text-sm text-right font-semibold ${e.jenis.startsWith('PENDAPATAN') ? 'text-green-600' : 'text-red-600'}`}>
                    {e.jenis.startsWith('PENDAPATAN') ? '+' : '-'}{formatRupiah(e.jumlah)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {e.bukti_urls?.length > 0 ? <BuktiGallery urls={e.bukti_urls} /> : <FileUpload onUpload={(file) => handleUploadBukti(file, e.id)} uploading={uploading} />}
                  </td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={e.status} /></td>
                  {isOwner && (
                    <td className="px-4 py-3 text-center">
                      {e.status === 'PENDING' && (
                        <div className="flex gap-1 justify-center">
                          <button onClick={() => handleVerify(e.id, 'REJECTED')} className="p-1 text-red-600 hover:bg-red-50 rounded"><XCircle size={16} /></button>
                          <button onClick={() => handleVerify(e.id, 'VERIFIED')} className="p-1 text-green-600 hover:bg-green-50 rounded"><CheckCircle size={16} /></button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Tambah Entry Akuntansi">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select label="Jenis" value={formData.jenis} onChange={(e) => setFormData({...formData, jenis: e.target.value})} options={jenisOptions} />
          <Input label="Jumlah (Rp)" type="number" value={formData.jumlah} onChange={(e) => setFormData({...formData, jumlah: e.target.value})} required />
          <Input label="Keterangan" value={formData.keterangan} onChange={(e) => setFormData({...formData, keterangan: e.target.value})} required />
          <Input label="Tanggal" type="date" value={formData.tanggal} onChange={(e) => setFormData({...formData, tanggal: e.target.value})} required />
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowModal(false)}>Batal</Button>
            <Button type="submit" className="flex-1" loading={submitting}><Save size={18} /> Simpan</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== TARIK DATA ====================

const TarikDataPage = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('loket');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState({
    mulai: new Date().toISOString().split('T')[0].slice(0, 7) + '-01',
    akhir: new Date().toISOString().split('T')[0]
  });
  // State for verification
  const [pendingLaporan, setPendingLaporan] = useState([]);
  const [verifikasiLoading, setVerifikasiLoading] = useState(false);
  const [selectedLaporan, setSelectedLaporan] = useState(null);
  const [showVerifikasiModal, setShowVerifikasiModal] = useState(false);
  const [verifikasiAction, setVerifikasiAction] = useState('');
  const [verifikasiCatatan, setVerifikasiCatatan] = useState('');
  // Shift filter for PENJAGA_LOKET
  const [shiftFilter, setShiftFilter] = useState('ALL');

  const loadData = async () => {
    setLoading(true);
    setError('');
    setData(null);
    try {
      let endpoint = `/api/tarik-data/${activeTab}?tanggal_mulai=${dateRange.mulai}&tanggal_akhir=${dateRange.akhir}`;
      // Add shift filter for PENJAGA_LOKET
      if (currentUser?.role === 'PENJAGA_LOKET' && activeTab === 'loket' && shiftFilter !== 'ALL') {
        endpoint += `&shift_period=${shiftFilter}`;
      }
      const result = await api.get(endpoint);
      setData(result);
    } catch (err) {
      console.error('Tarik Data Error:', err);
      setError(err.message || 'Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  // Load pending laporan for verification
  const loadPendingLaporan = async () => {
    setVerifikasiLoading(true);
    try {
      const result = await api.get('/api/dashboard/kasir');
      setPendingLaporan(result.pending_laporan || []);
    } catch (err) {
      console.error('Error loading pending laporan:', err);
    } finally {
      setVerifikasiLoading(false);
    }
  };

  // Handle verification (approve/reject)
  const handleVerifikasi = async () => {
    if (!selectedLaporan || !verifikasiAction) return;
    try {
      await api.put(`/api/laporan-shift/${selectedLaporan.id}/verify?status=${verifikasiAction}&catatan=${encodeURIComponent(verifikasiCatatan || '')}`);
      setShowVerifikasiModal(false);
      setSelectedLaporan(null);
      setVerifikasiAction('');
      setVerifikasiCatatan('');
      // Refresh pending laporan
      loadPendingLaporan();
      alert(`Laporan berhasil di${verifikasiAction === 'APPROVED' ? 'setujui' : 'tolak'}!`);
    } catch (err) {
      console.error('Verification error:', err);
      alert('Gagal memverifikasi laporan: ' + (err.message || err.detail || 'Unknown error'));
    }
  };

  const openVerifikasiModal = (laporan, action) => {
    setSelectedLaporan(laporan);
    setVerifikasiAction(action);
    setVerifikasiCatatan('');
    setShowVerifikasiModal(true);
  };

  // Load pending laporan when tab changes to verifikasi
  useEffect(() => {
    if (activeTab === 'verifikasi' && ['KASIR', 'SUPERKASIR', 'MANAGER', 'OWNER'].includes(currentUser?.role)) {
      loadPendingLaporan();
    }
  }, [activeTab, currentUser?.role]);

  const tabs = currentUser?.role === 'PENJAGA_LOKET'
    ? [{ id: 'loket', label: 'Laporan Shift' }]
    : currentUser?.role === 'KASIR'
    ? [{ id: 'loket', label: 'Loket' }, { id: 'pengeluaran', label: 'Pengeluaran' }, { id: 'verifikasi', label: 'Verifikasi' }]
    : [{ id: 'loket', label: 'Loket' }, { id: 'pengeluaran', label: 'Pengeluaran' }, { id: 'verifikasi', label: 'Verifikasi' }, { id: 'akuntansi', label: 'Akuntansi' }];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Tarik Data</h1>

      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <Input label="Tanggal Mulai" type="date" value={dateRange.mulai} onChange={(e) => setDateRange({...dateRange, mulai: e.target.value})} className="w-40" />
          <Input label="Tanggal Akhir" type="date" value={dateRange.akhir} onChange={(e) => setDateRange({...dateRange, akhir: e.target.value})} className="w-40" />
          {/* Shift Filter for PENJAGA_LOKET */}
          {currentUser?.role === 'PENJAGA_LOKET' && (
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Filter Shift</label>
              <select
                value={shiftFilter}
                onChange={(e) => setShiftFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-32"
              >
                <option value="ALL">Semua</option>
                <option value="PAGI">Pagi</option>
                <option value="SIANG">Siang</option>
              </select>
            </div>
          )}
          <Button onClick={loadData} loading={loading}><Download size={18} /> Tarik Data</Button>
        </div>
      </Card>

      {/* Tabs - Hide for PENJAGA_LOKET */}
      {currentUser?.role !== 'PENJAGA_LOKET' && (
        <div className="flex gap-2">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => { setActiveTab(tab.id); setData(null); }} className={`px-4 py-2 rounded-lg font-medium ${activeTab === tab.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {loading && <LoadingSpinner />}

      {data && activeTab === 'loket' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard title="Total Records" value={data.total_records} icon={FileText} color="blue" />
            <StatCard title="Total Omzet" value={formatRupiah(data.summary?.total_omzet || 0)} icon={DollarSign} color="green" />
            <StatCard title="Total Transaksi" value={data.summary?.total_transaksi || 0} icon={CreditCard} color="purple" />
            <StatCard title="Total Tunai" value={formatRupiah(data.summary?.total_tunai || 0)} icon={Wallet} color="yellow" />
          </div>
          <Card className="p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Data per Hari</h3>
            <div className="space-y-4">
              {data.data?.map(d => (
                <div key={d.tanggal} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-medium">{formatDate(d.tanggal)}</p>
                    <p className="font-bold text-primary-600">{formatRupiah(d.total_omzet)}</p>
                  </div>
                  <p className="text-sm text-gray-500">{d.total_transaksi} transaksi • Tunai: {formatRupiah(d.total_tunai)} • Non-Tunai: {formatRupiah(d.total_non_tunai)}</p>
                  {d.laporan?.length > 0 && (
                    <div className="mt-2 pt-2 border-t space-y-1">
                      {d.laporan.map(l => (
                        <div key={l.id} className="flex justify-between text-sm">
                          <span>{l.loket_nama} - {l.shift} ({l.agent})</span>
                          <div className="flex items-center gap-2">
                            <span>{formatRupiah(l.omzet)}</span>
                            <StatusBadge status={l.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {data && activeTab === 'pengeluaran' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Total Pengeluaran" value={formatRupiah(data.summary?.total || 0)} icon={Wallet} color="red" />
            <StatCard title="Verified" value={formatRupiah(data.summary?.verified || 0)} icon={CheckCircle} color="green" />
            <StatCard title="Pending" value={formatRupiah(data.summary?.pending || 0)} icon={Clock} color="yellow" />
          </div>
          <Card className="p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Per Jenis Pengeluaran</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.by_jenis?.map(j => (
                <div key={j.jenis} className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">{j.jenis.replace('_', ' ')}</p>
                  <p className="text-xl font-bold">{formatRupiah(j.total)}</p>
                  <p className="text-xs text-gray-500">{j.count} transaksi</p>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {data && activeTab === 'akuntansi' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Total Pendapatan" value={formatRupiah(data.summary?.total_pendapatan || 0)} icon={TrendingUp} color="green" />
            <StatCard title="Total Biaya" value={formatRupiah(data.summary?.total_biaya || 0)} icon={TrendingDown} color="red" />
            <StatCard title="Pending" value={data.summary?.pending_count || 0} icon={Clock} color="yellow" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-5">
              <h3 className="font-semibold text-green-700 mb-4">Pendapatan</h3>
              {data.pendapatan?.length > 0 ? data.pendapatan.map(p => (
                <div key={p.id} className="flex justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{p.keterangan}</p>
                    <p className="text-xs text-gray-500">{formatDate(p.tanggal)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600">+{formatRupiah(p.jumlah)}</p>
                    <StatusBadge status={p.status} />
                  </div>
                </div>
              )) : <p className="text-gray-500 text-sm">Tidak ada data</p>}
            </Card>
            <Card className="p-5">
              <h3 className="font-semibold text-red-700 mb-4">Biaya</h3>
              {data.biaya?.length > 0 ? data.biaya.map(b => (
                <div key={b.id} className="flex justify-between py-2 border-b last:border-0">
                  <div>
                    <p className="text-sm font-medium">{b.keterangan}</p>
                    <p className="text-xs text-gray-500">{formatDate(b.tanggal)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-red-600">-{formatRupiah(b.jumlah)}</p>
                    <StatusBadge status={b.status} />
                  </div>
                </div>
              )) : <p className="text-gray-500 text-sm">Tidak ada data</p>}
            </Card>
          </div>
        </>
      )}

      {activeTab === 'verifikasi' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Laporan Pending" value={pendingLaporan.length} icon={Clock} color="yellow" />
            <StatCard title="Total Omzet" value={formatRupiah(pendingLaporan.reduce((sum, l) => sum + (l.total_omzet || 0), 0))} icon={DollarSign} color="green" />
            <StatCard title="Total Transaksi" value={pendingLaporan.reduce((sum, l) => sum + (l.total_transaksi || 0), 0)} icon={CreditCard} color="blue" />
          </div>

          {verifikasiLoading ? (
            <LoadingSpinner />
          ) : pendingLaporan.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <p className="text-gray-500">Tidak ada laporan yang perlu diverifikasi</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingLaporan.map(laporan => (
                <Card key={laporan.id} className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-gray-900">{laporan.loket_nama}</span>
                        <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">PENDING</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {formatDate(laporan.tanggal)} - Shift {laporan.shift_period} | Agent: {laporan.agent_nama}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        No. Laporan: {laporan.nomor_laporan}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-sm">
                        <div>
                          <p className="text-gray-500">Omzet</p>
                          <p className="font-semibold">{formatRupiah(laporan.total_omzet || 0)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Transaksi</p>
                          <p className="font-semibold">{laporan.total_transaksi || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Tunai</p>
                          <p className="font-semibold">{formatRupiah(laporan.total_tunai || 0)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Non-Tunai</p>
                          <p className="font-semibold">{formatRupiah(laporan.total_non_tunai || 0)}</p>
                        </div>
                      </div>
                      {laporan.catatan && (
                        <p className="text-sm text-gray-500 mt-2 italic">Catatan: {laporan.catatan}</p>
                      )}
                      {laporan.last_verification_note && (
                        <p className="text-sm text-red-500 mt-2">Verifikasi sebelumnya: {laporan.last_verification_note}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={() => openVerifikasiModal(laporan, 'APPROVED')} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle size={18} /> Setuju
                      </Button>
                      <Button onClick={() => openVerifikasiModal(laporan, 'REJECTED')} className="bg-red-600 hover:bg-red-700">
                        <XCircle size={18} /> Tolak
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Verifikasi Modal */}
      {showVerifikasiModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">
              {verifikasiAction === 'APPROVED' ? 'Setuju Laporan' : 'Tolak Laporan'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Anda akan {verifikasiAction === 'APPROVED' ? 'menyetuju' : 'menolak'} laporan:
              <br />
              <span className="font-semibold">{selectedLaporan?.nomor_laporan}</span>
              <br />
              {selectedLaporan?.loket_nama} - {selectedLaporan?.shift_period}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Catatan (opsional)
              </label>
              <textarea
                value={verifikasiCatatan}
                onChange={(e) => setVerifikasiCatatan(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="3"
                placeholder={verifikasiAction === 'REJECTED' ? 'Alasan penolakan...' : 'Catatan verifikasi...'}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button onClick={() => setShowVerifikasiModal(false)} className="bg-gray-500 hover:bg-gray-600">
                Batal
              </Button>
              <Button
                onClick={handleVerifikasi}
                className={verifikasiAction === 'APPROVED' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {verifikasiAction === 'APPROVED' ? 'Setuju' : 'Tolak'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {!data && !loading && !error && (
        <Card className="p-8 text-center">
          <Download className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Pilih periode dan klik "Tarik Data" untuk melihat laporan</p>
        </Card>
      )}
    </div>
  );
};

// ==================== MAIN APP ====================

// ==================== PENGATURAN (OWNER) ====================

const PengaturanPage = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('shift');
  const [shiftSettings, setShiftSettings] = useState([]);
  const [menuSettings, setMenuSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [shifts, menus] = await Promise.all([
        api.get('/api/settings/shifts'),
        api.get('/api/settings/menu-verification')
      ]);
      setShiftSettings(shifts);
      setMenuSettings(menus);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShift = async (shiftId, field, value) => {
    setSubmitting(true);
    try {
      await api.put(`/api/settings/shifts/${shiftId}`, { [field]: value });
      setSuccess('Pengaturan shift berhasil diupdate!');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMenuVerification = async (settingId, field, value) => {
    setSubmitting(true);
    try {
      await api.put(`/api/settings/menu-verification/${settingId}?${field}=${value}`);
      setSuccess('Pengaturan verifikasi menu berhasil diupdate!');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">⚙️ Pengaturan Sistem</h1>
      
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Tab Navigation */}
      <div className="flex gap-2">
        <button onClick={() => setActiveTab('shift')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'shift' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <Timer size={16} className="inline mr-2" /> Waktu Shift
        </button>
        <button onClick={() => setActiveTab('verification')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'verification' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
          <CheckCircle size={16} className="inline mr-2" /> Pengaturan Verifikasi
        </button>
      </div>

      {/* Shift Settings Tab */}
      {activeTab === 'shift' && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Pengaturan Waktu Shift</h3>
          <p className="text-sm text-gray-500 mb-4">Atur waktu mulai dan selesai untuk setiap shift. Perubahan akan berlaku untuk seluruh loket.</p>
          
          <div className="space-y-4">
            {shiftSettings.map(shift => (
              <div key={shift.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      shift.shift_name === 'PAGI' ? 'bg-yellow-100' : 
                      shift.shift_name === 'SIANG' ? 'bg-orange-100' : 'bg-blue-100'
                    }`}>
                      {shift.shift_name === 'PAGI' ? '🌅' : shift.shift_name === 'SIANG' ? '☀️' : '🌙'}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Shift {shift.shift_name}</p>
                      <p className="text-sm text-gray-500">
                        {shift.jam_mulai} - {shift.jam_selesai}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-500">Mulai:</label>
                      <input 
                        type="time" 
                        value={shift.jam_mulai} 
                        onChange={(e) => handleUpdateShift(shift.id, 'jam_mulai', e.target.value)}
                        className="px-2 py-1 border rounded-lg text-sm"
                        disabled={submitting}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-500">Selesai:</label>
                      <input 
                        type="time" 
                        value={shift.jam_selesai} 
                        onChange={(e) => handleUpdateShift(shift.id, 'jam_selesai', e.target.value)}
                        className="px-2 py-1 border rounded-lg text-sm"
                        disabled={submitting}
                      />
                    </div>
                    <button
                      onClick={() => handleUpdateShift(shift.id, 'is_active', !shift.is_active)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${shift.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}
                      disabled={submitting}
                    >
                      {shift.is_active ? 'Aktif' : 'Nonaktif'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Menu Verification Settings Tab */}
      {activeTab === 'verification' && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Pengaturan Alur Verifikasi</h3>
          <p className="text-sm text-gray-500 mb-4">Tentukan role mana saja yang perlu memberikan approval untuk setiap jenis transaksi.</p>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Menu / Transaksi</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Verifikasi Kasir</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Verifikasi Manager</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Verifikasi Owner</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {menuSettings.map(setting => (
                  <tr key={setting.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{setting.menu_label}</p>
                      <p className="text-xs text-gray-500">{setting.menu_key}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleUpdateMenuVerification(setting.id, 'require_kasir', !setting.require_kasir_approval)}
                        className={`w-8 h-8 rounded-lg ${setting.require_kasir_approval ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                        disabled={submitting}
                      >
                        {setting.require_kasir_approval ? <Check size={18} className="mx-auto" /> : '○'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleUpdateMenuVerification(setting.id, 'require_manager', !setting.require_manager_approval)}
                        className={`w-8 h-8 rounded-lg ${setting.require_manager_approval ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                        disabled={submitting}
                      >
                        {setting.require_manager_approval ? <Check size={18} className="mx-auto" /> : '○'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleUpdateMenuVerification(setting.id, 'require_owner', !setting.require_owner_approval)}
                        className={`w-8 h-8 rounded-lg ${setting.require_owner_approval ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                        disabled={submitting}
                      >
                        {setting.require_owner_approval ? <Check size={18} className="mx-auto" /> : '○'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Catatan:</strong> Centang menandakan verifikasi diperlukan dari role tersebut sebelum transaksi dianggap final.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
};

// ==================== DEPOSIT PROVIDER PAGE ====================

const DepositBankPage = ({ currentUser }) => {
  const [deposits, setDeposits] = useState([]);
  const [providers, setProviders] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDeposit, setSelectedDeposit] = useState(null);

  const [approveData, setApproveData] = useState({
    deposit_id: '',
    jumlah_topup: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [depositsData, providersData, pendingData, allData] = await Promise.all([
        api.get('/api/deposits'),
        api.get('/api/providers'),
        api.get('/api/deposit-topup-requests?status=PENDING_MANAGER'),
        api.get('/api/deposit-topup-requests?limit=50')
      ]);
      setDeposits(depositsData);
      setProviders(providersData.filter(p => p.is_active));
      setPendingRequests(pendingData);
      setAllRequests(allData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = (deposit) => {
    setSelectedDeposit(deposit);
  };

  const handleApproveRequest = (request) => {
    setSelectedRequest(request);
    // Show modal to specify jumlah_topup
    setApproveData({ jumlah_topup: '' });
    setShowApproveModal(true);
  };

  const approveRequest = async (requestId, jumlahTopup) => {
    setSubmitting(true);
    try {
      // Convert to number to ensure proper type
      const jumlahTopupNumber = parseFloat(jumlahTopup);

      const params = new URLSearchParams();
      params.append('jumlah_topup', jumlahTopupNumber.toString());

      await api.put(`/api/deposit-topup-requests/${requestId}/approve?${params.toString()}`);
      setSuccess(`Request deposit berhasil disetujui! Jumlah: ${formatRupiah(jumlahTopupNumber)}`);
      setShowApproveModal(false);
      setSelectedRequest(null);
      setApproveData({ jumlah_topup: '' });
      loadData();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectRequest = async (requestId) => {
    const reason = prompt('Alasan penolakan:');
    if (reason === null) return; // Cancelled

    setSubmitting(true);
    try {
      await api.put(`/api/deposit-topup-requests/${requestId}/reject`, { rejection_note: reason });
      setSuccess('Request deposit berhasil ditolak!');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  const canManage = currentUser?.role === 'OWNER' || currentUser?.role === 'MANAGER';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">💰 Deposit Bank</h1>
          <p className="text-sm text-gray-500">Kelola saldo deposit untuk setiap provider</p>
        </div>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Pending Requests Section */}
      {pendingRequests.length > 0 && (
        <Card className="p-5 bg-yellow-50 border-2 border-yellow-400">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="text-yellow-600" size={24} />
              <div>
                <h3 className="font-bold text-gray-900">Request Deposit Pending</h3>
                <p className="text-sm text-gray-600">{pendingRequests.length} permintaan menunggu persetujuan</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {pendingRequests.map(req => (
              <div key={req.id} className="bg-white rounded-lg p-4 border border-yellow-200 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900">{req.provider_nama || 'Belum ditentukan'}</h4>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Pending</span>
                      {!req.deposit_id && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Perlu ditentukan</span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {req.jumlah_topup ? (
                        <div>
                          <span className="text-gray-500">Jumlah: </span>
                          <span className="font-semibold text-gray-900">{formatRupiah(req.jumlah_topup)}</span>
                        </div>
                      ) : (
                        <div className="text-orange-600">
                          <span className="font-medium">⚠️ Jumlah belum ditentukan</span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-500">Bank Tujuan: </span>
                        <span className="font-semibold">
                          {req.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Requester: </span>
                        <span className="text-gray-900">{req.requested_by_nama}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Tanggal: </span>
                        <span className="text-gray-900">{formatDateTime(req.requested_at)}</span>
                      </div>
                    </div>

                    {req.keterangan && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Keterangan: </span>{req.keterangan}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      onClick={() => handleApproveRequest(req)}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle size={16} /> {req.deposit_id && req.jumlah_topup ? 'Approve' : 'Tentukan & Approve'}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleRejectRequest(req.id)}
                      disabled={submitting}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <XCircle size={16} /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Deposit Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {deposits.map(dep => (
          <Card key={dep.id} className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="font-semibold text-gray-900">{dep.provider_nama || 'Unknown Provider'}</p>
                <p className="text-sm text-gray-500">{dep.provider_kategori}</p>
              </div>
              <span className={`w-3 h-3 rounded-full ${dep.saldo > 1000000 ? 'bg-green-500' : dep.saldo > 500000 ? 'bg-yellow-500' : 'bg-red-500'}`}></span>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">Saldo Saat Ini</p>
              <p className={`text-2xl font-bold ${dep.saldo > 1000000 ? 'text-green-600' : dep.saldo > 500000 ? 'text-yellow-600' : 'text-red-600'}`}>
                {formatRupiah(dep.saldo)}
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => handleViewHistory(dep)} className="flex-1">
                <Eye size={16} /> Riwayat
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {deposits.length === 0 && (
        <Card className="p-8 text-center">
          <Banknote className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Belum ada deposit bank. Tambahkan deposit pertama!</p>
        </Card>
      )}

      {/* Riwayat Request Top-up Bank */}
      {allRequests.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <History size={20} className="text-blue-600" />
            Riwayat Request Top-up Bank
          </h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {allRequests
              .filter(req => req.status !== 'PENDING_MANAGER')
              .sort((a, b) => new Date(b.requested_at) - new Date(a.requested_at))
              .map(req => {
                const statusConfig = {
                  'APPROVED': { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', label: '✓ Disetujui' },
                  'REJECTED': { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', label: '✗ Ditolak' },
                  'COMPLETED': { bg: 'bg-green-50', border: 'border-green-300', text: 'text-green-800', label: '✓ Selesai Dieksekusi' }
                };
                const config = statusConfig[req.status] || { bg: 'bg-gray-50', border: 'border-gray-300', text: 'text-gray-800', label: req.status };

                return (
                  <div key={req.id} className={`p-4 rounded-lg border ${config.bg} ${config.border}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-1 text-xs rounded-full ${config.text} ${config.bg}`}>
                            {config.label}
                          </span>
                          <span className="text-sm text-gray-500">
                            {req.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          {req.jumlah_topup && (
                            <div>
                              <span className="text-gray-500">Jumlah: </span>
                              <span className="font-semibold text-green-600">{formatRupiah(req.jumlah_topup)}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">Requester: </span>
                            <span className="text-gray-900">{req.requested_by_nama}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Tanggal: </span>
                            <span className="text-gray-900">{formatDateTime(req.requested_at)}</span>
                          </div>
                          {req.status === 'APPROVED' && req.approved_by_nama && (
                            <div>
                              <span className="text-gray-500">Disetujui oleh: </span>
                              <span className="text-gray-900">{req.approved_by_nama}</span>
                            </div>
                          )}
                          {req.status === 'REJECTED' && req.approved_by_nama && (
                            <div>
                              <span className="text-gray-500">Ditolak oleh: </span>
                              <span className="text-gray-900">{req.approved_by_nama}</span>
                            </div>
                          )}
                          {req.status === 'COMPLETED' && req.executed_by_nama && (
                            <div>
                              <span className="text-gray-500">Dieksekusi oleh: </span>
                              <span className="text-gray-900">{req.executed_by_nama}</span>
                            </div>
                          )}
                        </div>
                        {req.rejection_note && (
                          <div className="mt-2 text-sm text-red-600">
                            <span className="font-medium">Alasan penolakan: </span>{req.rejection_note}
                          </div>
                        )}
                        {req.keterangan && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Keterangan: </span>{req.keterangan}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </Card>
      )}

      {/* History Modal */}
      <Modal isOpen={!!selectedDeposit} onClose={() => setSelectedDeposit(null)} title={`Riwayat - ${selectedDeposit?.provider_nama}`}>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {selectedDeposit?.history?.map((h, idx) => (
            <div key={idx} className={`p-3 rounded-lg ${h.jenis === 'TOPUP' ? 'bg-green-50' : h.jenis === 'USAGE' ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-sm">{h.jenis}</p>
                  <p className="text-xs text-gray-500">{h.keterangan}</p>
                  <p className="text-xs text-gray-400">{formatDateTime(h.tanggal)}</p>
                </div>
                <div className="text-right">
                  <p className={`font-semibold ${h.jenis === 'TOPUP' || h.jenis === 'INITIAL' ? 'text-green-600' : 'text-red-600'}`}>
                    {h.jenis === 'TOPUP' || h.jenis === 'INITIAL' ? '+' : '-'}{formatRupiah(h.jumlah)}
                  </p>
                  <p className="text-xs text-gray-500">Saldo: {formatRupiah(h.saldo_after)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Oleh: {h.by}</p>
            </div>
          ))}
          {(!selectedDeposit?.history || selectedDeposit.history.length === 0) && (
            <p className="text-gray-500 text-center py-4">Belum ada riwayat transaksi</p>
          )}
        </div>
      </Modal>

      {/* Approve Modal - Specify Provider and Amount */}
      {showApproveModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-green-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <CheckCircle size={20} />
                Setujui Request Deposit
              </h3>
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setSelectedRequest(null);
                  setApproveData({ deposit_id: '', jumlah_topup: '' });
                  setError('');
                }}
                className="text-white hover:bg-white/20 p-1 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">Request dari Penjaga Loket</p>
                <div className="text-sm space-y-1">
                  <div>
                    <span className="text-gray-500">Bank Tujuan: </span>
                    <span className="font-semibold text-base">
                      {selectedRequest.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                    </span>
                  </div>
                  {selectedRequest.keterangan && (
                    <div>
                      <span className="text-gray-500">Keterangan: </span>
                      <span className="text-gray-900">{selectedRequest.keterangan}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Requester: </span>
                    <span className="text-gray-900">{selectedRequest.requested_by_nama}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Jumlah Top-Up (Rp) *
                </label>
                <input
                  type="number"
                  value={approveData.jumlah_topup}
                  onChange={(e) => setApproveData({ ...approveData, jumlah_topup: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Contoh: 1000000"
                  min="0"
                  step="1000"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Masukkan nominal top-up untuk Bank Tujuan di atas
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Info:</strong> Setelah disetujui, Kasir akan memproses dan upload bukti transfer.
                  Saldo bank tujuan akan otomatis terupdate di menu Laporan Shift Penjaga Loket.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowApproveModal(false);
                    setSelectedRequest(null);
                    setApproveData({ jumlah_topup: '' });
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={() => approveRequest(selectedRequest.id, approveData.jumlah_topup)}
                  disabled={submitting || !approveData.jumlah_topup}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Menyetujui...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Setujui Request
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== TOPUP DEPOSIT PAGE (KASIR) ====================

const TopupDepositPage = ({ currentUser }) => {
  const [requests, setRequests] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reqData, depData] = await Promise.all([
        api.get('/api/deposit-topup-requests'),
        api.get('/api/deposits')
      ]);
      setRequests(reqData);
      setDeposits(depData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUploadModal = (request) => {
    setSelectedRequest(request);
    setShowUploadModal(true);
  };

  const handleExecuteTopup = async () => {
    if (!uploadFile) {
      setError('Harap upload bukti transfer terlebih dahulu');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('bukti_transfer', uploadFile);

      await api.put(`/api/deposit-topup-requests/${selectedRequest.id}/execute`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess('Top-up deposit berhasil dilakukan!');
      setShowUploadModal(false);
      setSelectedRequest(null);
      setUploadFile(null);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  // Helper to get current bank balance
  const getBankBalance = (bankTujuan) => {
    const deposit = deposits.find(d => {
      if (bankTujuan === 'BRIS') {
        return d.provider_nama === 'BSI' || d.provider_nama.includes('BSI');
      } else if (bankTujuan === 'MANDIRI') {
        return d.provider_nama.includes('Mandiri');
      }
      return false;
    });
    return deposit ? deposit.saldo : 0;
  };

  const approvedRequests = requests.filter(r => r.status === 'APPROVED');
  const pendingRequests = requests.filter(r => r.status === 'PENDING_MANAGER');
  const completedRequests = requests.filter(r => r.status === 'COMPLETED');

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">🔄 Top-Up Deposit Bank</h1>
      
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Approved Requests - Ready to Execute */}
      {approvedRequests.length > 0 && (
        <Card className="p-5 border-l-4 border-l-green-500">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="text-green-600" size={20} />
            Request Disetujui - Siap Eksekusi ({approvedRequests.length})
          </h3>
          <div className="space-y-3">
            {approvedRequests.map(req => {
              const saldoAwal = getBankBalance(req.bank_tujuan);
              const saldoSetelah = saldoAwal + req.jumlah_topup;
              return (
                <div key={req.id} className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900">{req.provider_nama}</h4>
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">Approved</span>
                        <span className="text-sm text-gray-500">
                          {req.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                        </span>
                      </div>

                      {/* Saldo Info */}
                      <div className="grid grid-cols-3 gap-3 mb-3 p-3 bg-white rounded-lg border border-green-200">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Saldo Awal</p>
                          <p className="text-sm font-semibold text-gray-700">{formatRupiah(saldoAwal)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500 mb-1">Top-up</p>
                          <p className="text-sm font-semibold text-green-600">+{formatRupiah(req.jumlah_topup)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500 mb-1">Saldo Akhir</p>
                          <p className="text-sm font-bold text-green-700">{formatRupiah(saldoSetelah)}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <div className="col-span-2">
                          <span className="text-gray-500">Requester: </span>
                          <span className="text-gray-900">{req.requested_by_nama}</span>
                          {req.approved_by_nama && (
                            <>
                              <span className="text-gray-400 mx-2">•</span>
                              <span className="text-gray-500">Disetujui oleh: </span>
                              <span className="text-gray-900">{req.approved_by_nama}</span>
                            </>
                          )}
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Tanggal: </span>
                          <span className="text-gray-900">{formatDateTime(req.requested_at)}</span>
                        </div>
                      </div>
                      {req.keterangan && (
                        <p className="text-xs text-gray-600 mt-2">
                          <span className="font-medium">Keterangan: </span>{req.keterangan}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => handleOpenUploadModal(req)} size="sm" className="bg-blue-600 hover:bg-blue-700">
                      <Upload size={16} /> Upload Bukti & Eksekusi
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Current Deposit Balances */}
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Saldo Deposit Saat Ini</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {deposits.map(dep => (
            <div key={dep.id} className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium">{dep.provider_nama}</p>
              <p className={`text-xl font-bold ${dep.saldo > 1000000 ? 'text-green-600' : dep.saldo > 500000 ? 'text-yellow-600' : 'text-red-600'}`}>
                {formatRupiah(dep.saldo)}
              </p>
            </div>
          ))}
        </div>
      </Card>

      {/* Pending Requests */}
      {pendingRequests.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="text-yellow-600" size={20} />
            Menunggu Approval Manager ({pendingRequests.length})
          </h3>
          <div className="space-y-2">
            {pendingRequests.map(req => (
              <div key={req.id} className="p-3 bg-yellow-50 rounded-lg flex justify-between items-center">
                <div>
                  <p className="font-medium">{req.provider_nama}</p>
                  <p className="text-sm text-gray-500">Top-up: {formatRupiah(req.jumlah_topup)}</p>
                </div>
                <StatusBadge status="PENDING" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Completed Requests */}
      {completedRequests.length > 0 && (
        <Card className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Riwayat Top-Up Selesai</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {completedRequests
              .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
              .slice(0, 20)
              .map(req => (
                <div key={req.id} className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-gray-500">
                          {req.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                        </span>
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded">Selesai</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-2 text-sm">
                        <div>
                          <p className="text-xs text-gray-500">Jumlah</p>
                          <p className="font-semibold text-green-600">{formatRupiah(req.jumlah_topup)}</p>
                        </div>
                        {req.saldo_awal !== undefined && req.saldo_akhir !== undefined && (
                          <>
                            <div>
                              <p className="text-xs text-gray-500">Saldo Awal</p>
                              <p className="font-semibold text-gray-700">{formatRupiah(req.saldo_awal)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">Saldo Akhir</p>
                              <p className="font-bold text-green-700">{formatRupiah(req.saldo_akhir)}</p>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                        <div>
                          <span>Requester: </span>
                          <span className="text-gray-900">{req.requested_by_nama}</span>
                        </div>
                        <div>
                          <span>Kasir: </span>
                          <span className="text-gray-900">{req.executed_by_nama || '-'}</span>
                        </div>
                        <div className="col-span-2">
                          <span>Tanggal Eksekusi: </span>
                          <span className="text-gray-900">{formatDateTime(req.completed_at)}</span>
                        </div>
                      </div>
                      {req.keterangan && (
                        <p className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Ket: </span>{req.keterangan}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </Card>
      )}

      {approvedRequests.length === 0 && pendingRequests.length === 0 && (
        <Card className="p-8 text-center">
          <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Tidak ada request top-up yang perlu dieksekusi</p>
        </Card>
      )}

      {/* Upload Proof Modal */}
      {showUploadModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-blue-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Upload size={20} />
                Upload Bukti Transfer
              </h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedRequest(null);
                  setUploadFile(null);
                  setError('');
                }}
                className="text-white hover:bg-white/20 p-1 rounded"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">{selectedRequest.provider_nama}</p>
                <div className="text-sm space-y-1">
                  <div>
                    <span className="text-gray-500">Jumlah: </span>
                    <span className="font-semibold text-green-600">{formatRupiah(selectedRequest.jumlah_topup)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Bank Tujuan: </span>
                    <span className="font-semibold">
                      {selectedRequest.bank_tujuan === 'BRIS' ? '💚 BSI' : '💙 Bank Mandiri'}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bukti Transfer *
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {uploadFile && (
                  <p className="mt-2 text-sm text-gray-600">File: {uploadFile.name}</p>
                )}
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Info:</strong> Setelah klik "Eksekusi Top-Up", saldo bank tujuan akan otomatis terupdate di menu Laporan Shift Penjaga Loket.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedRequest(null);
                    setUploadFile(null);
                    setError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Batal
                </button>
                <button
                  onClick={handleExecuteTopup}
                  disabled={submitting || !uploadFile}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Mengeksekusi...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Eksekusi Top-Up
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== SHIFT SETTINGS PAGE (MANAGER) ====================

const ShiftSettingsPage = ({ currentUser }) => {
  const [shiftSettings, setShiftSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const shifts = await api.get('/api/settings/shifts');
      setShiftSettings(shifts);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateShift = async (shiftId, field, value) => {
    setSubmitting(true);
    try {
      await api.put(`/api/settings/shifts/${shiftId}`, { [field]: value });
      setSuccess('Pengaturan shift berhasil diupdate!');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">⏰ Pengaturan Waktu Shift</h1>
      
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <Card className="p-5">
        <p className="text-sm text-gray-500 mb-4">Atur waktu mulai dan selesai untuk setiap shift di menu Penjaga Loket.</p>
        
        <div className="space-y-4">
          {shiftSettings.map(shift => (
            <div key={shift.id} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                    shift.shift_name === 'PAGI' ? 'bg-yellow-100' : 
                    shift.shift_name === 'SIANG' ? 'bg-orange-100' : 'bg-blue-100'
                  }`}>
                    {shift.shift_name === 'PAGI' ? '🌅' : shift.shift_name === 'SIANG' ? '☀️' : '🌙'}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-lg">Shift {shift.shift_name}</p>
                    <p className="text-sm text-gray-500">
                      Saat ini: {shift.jam_mulai} - {shift.jam_selesai}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Jam Mulai</label>
                    <input 
                      type="time" 
                      value={shift.jam_mulai} 
                      onChange={(e) => handleUpdateShift(shift.id, 'jam_mulai', e.target.value)}
                      className="px-3 py-2 border rounded-lg"
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Jam Selesai</label>
                    <input 
                      type="time" 
                      value={shift.jam_selesai} 
                      onChange={(e) => handleUpdateShift(shift.id, 'jam_selesai', e.target.value)}
                      className="px-3 py-2 border rounded-lg"
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Status</label>
                    <button
                      onClick={() => handleUpdateShift(shift.id, 'is_active', !shift.is_active)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${shift.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}
                      disabled={submitting}
                    >
                      {shift.is_active ? '✓ Aktif' : 'Nonaktif'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ==================== DEPOSIT INFO PAGE (PENJAGA LOKET) ====================

const DepositInfoPage = ({ currentUser }) => {
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [topupBank, setTopupBank] = useState('BRIS');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/deposits');
      setDeposits(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestTopup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      await api.post('/api/deposit-topup-requests', {
        deposit_id: selectedDeposit.id,
        jumlah_topup: parseFloat(topupAmount),
        keterangan: topupNote,
        bank_tujuan: topupBank
      });
      setSuccess('Request top-up berhasil diajukan! Menunggu approval Manager.');
      setShowRequestModal(false);
      setTopupAmount('');
      setTopupNote('');
      setTopupBank('BRIS');
      setSelectedDeposit(null);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">💰 Info Deposit Bank</h1>
      <p className="text-gray-500">Lihat saldo deposit dan ajukan request top-up jika diperlukan.</p>
      
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {deposits.map(dep => (
          <Card key={dep.id} className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-gray-900">{dep.provider_nama}</p>
              <span className={`w-3 h-3 rounded-full ${dep.saldo > 1000000 ? 'bg-green-500' : dep.saldo > 500000 ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`}></span>
            </div>
            
            <p className={`text-2xl font-bold mb-4 ${dep.saldo > 1000000 ? 'text-green-600' : dep.saldo > 500000 ? 'text-yellow-600' : 'text-red-600'}`}>
              {formatRupiah(dep.saldo)}
            </p>
            
            {dep.saldo < 500000 && (
              <div className="mb-3 p-2 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 font-medium">⚠️ Saldo rendah! Segera ajukan top-up.</p>
              </div>
            )}
            
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={() => { setSelectedDeposit(dep); setShowRequestModal(true); }}
              className="w-full"
            >
              <ArrowUpRight size={16} /> Request Top-Up
            </Button>
          </Card>
        ))}
      </div>

      {deposits.length === 0 && (
        <Card className="p-8 text-center">
          <Banknote className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Belum ada data deposit bank</p>
        </Card>
      )}

      {/* Request Topup Modal */}
      <Modal isOpen={showRequestModal} onClose={() => { setShowRequestModal(false); setSelectedDeposit(null); }} title="Request Top-Up Deposit">
        <form onSubmit={handleRequestTopup} className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="font-medium">{selectedDeposit?.provider_nama}</p>
            <p className="text-sm text-gray-500">Saldo saat ini: <span className="font-semibold">{formatRupiah(selectedDeposit?.saldo || 0)}</span></p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Tujuan *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setTopupBank('BRIS')}
                className={`p-3 border-2 rounded-lg text-center transition-all ${
                  topupBank === 'BRIS'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 hover:border-green-300'
                }`}
              >
                <div className="font-semibold">💚 BSI</div>
              </button>
              <button
                type="button"
                onClick={() => setTopupBank('MANDIRI')}
                className={`p-3 border-2 rounded-lg text-center transition-all ${
                  topupBank === 'MANDIRI'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="font-semibold">💙 Bank Mandiri</div>
              </button>
            </div>
          </div>

          <Input
            label="Jumlah Top-Up (Rp)"
            type="number"
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
            placeholder="500000"
            required
          />
          <Input
            label="Keterangan"
            value={topupNote}
            onChange={(e) => setTopupNote(e.target.value)}
            placeholder="Alasan request top-up"
          />

          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              Request akan dikirim ke Manager untuk approval. Setelah disetujui, Kasir akan melakukan top-up.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={() => setShowRequestModal(false)}>Batal</Button>
            <Button type="submit" className="flex-1" loading={submitting}><Send size={18} /> Kirim Request</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// ==================== REQUEST TOP-UP BANK PAGE ====================

const RequestTopupBankPage = ({ currentUser }) => {
  const [bankTujuan, setBankTujuan] = useState('');
  const [keterangan, setKeterangan] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await api.post('/api/request-bank-topup', {
        bank_tujuan: bankTujuan,
        keterangan: keterangan
      });
      setSuccess('Request top-up bank berhasil dikirim! Menunggu persetujuan Manager/Owner.');
      setBankTujuan('');
      setKeterangan('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Request Top-up Saldo Bank</h1>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Bank Tujuan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Bank Tujuan <span className="text-red-500">*</span>
            </label>
            <div className="space-y-3">
              <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                bankTujuan === 'BRIS'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-green-300'
              }`}>
                <input
                  type="radio"
                  name="bank_tujuan"
                  value="BRIS"
                  checked={bankTujuan === 'BRIS'}
                  onChange={(e) => setBankTujuan(e.target.value)}
                  className="w-5 h-5 text-green-600"
                  required
                />
                <span className="text-2xl">💚</span>
                <span className="font-semibold text-gray-900">BSI</span>
              </label>

              <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                bankTujuan === 'MANDIRI'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300'
              }`}>
                <input
                  type="radio"
                  name="bank_tujuan"
                  value="MANDIRI"
                  checked={bankTujuan === 'MANDIRI'}
                  onChange={(e) => setBankTujuan(e.target.value)}
                  className="w-5 h-5 text-blue-600"
                  required
                />
                <span className="text-2xl">💙</span>
                <span className="font-semibold text-gray-900">Bank Mandiri</span>
              </label>
            </div>
          </div>

          {/* Keterangan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keterangan
            </label>
            <textarea
              value={keterangan}
              onChange={(e) => setKeterangan(e.target.value)}
              placeholder="Alasan request deposit (opsional)"
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Info */}
          <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
            <p className="text-sm text-blue-700">
              <strong>Info:</strong> Request top-up saldo bank akan memerlukan persetujuan Manager/Owner. Manager/Owner akan menentukan jumlah top-up. Setelah disetujui, Kasir akan memproses dan upload bukti transfer.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setBankTujuan('');
                setKeterangan('');
              }}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={submitting || !bankTujuan}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {submitting ? 'Mengirim...' : 'Kirim Request'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

function App() {
  const [currentUser, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showTanyaITModal, setShowTanyaITModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('currentUser');
    const token = localStorage.getItem('token');
    if (savedUser && token && savedUser !== 'undefined' && savedUser !== 'null') {
      let currentUserData;
      try {
        currentUserData = JSON.parse(savedUser);
      } catch (e) {
        localStorage.removeItem('currentUser');
        localStorage.removeItem('token');
        currentUserData = null;
      }
      if (currentUserData && currentUserData.role) {
        setUser(currentUserData);
        // Set default page based on role when loading from localStorage
        if (currentUserData.role === 'PENJAGA_LOKET') {
          setCurrentPage('dashboard-loket');
        } else if (currentUserData.role === 'KASIR' || currentUserData.role === 'SUPERKASIR') {
          setCurrentPage('dashboard-kasir');
        } else if (currentUserData.role === 'IT') {
          setCurrentPage('dashboard-it');
        } else if (currentUserData.role === 'TECHNICIAN') {
          setCurrentPage('technical-jobs');
        } else {
          setCurrentPage('dashboard');
        }
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (currentUserData) => {
    setUser(currentUserData);
    // Set default page based on role
    if (currentUserData.role === 'PENJAGA_LOKET') setCurrentPage('dashboard-loket');
    else if (currentUserData.role === 'KASIR' || currentUserData.role === 'SUPERKASIR') setCurrentPage('dashboard-kasir');
    else if (currentUserData.role === 'IT') setCurrentPage('dashboard-it');
    else if (currentUserData.role === 'TECHNICIAN') setCurrentPage('technical-jobs');
    else setCurrentPage('dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUser');
    setUser(null);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><LoadingSpinner size="lg" /></div>;

  if (!currentUser) return <LoginPage onLogin={handleLogin} />;

  const renderPage = () => {
    switch (currentPage) {
      // Owner/Manager
      case 'dashboard': return <DashboardOwner currentUser={currentUser} />;
      case 'verifikasi': return <VerifikasiPage currentUser={currentUser} />;
      case 'laporan-keuangan': return <LaporanKeuanganPage currentUser={currentUser} />;
      case 'loket': return <LoketPage currentUser={currentUser} />;
      case 'provider': return <ProviderPage currentUser={currentUser} />;
      case 'users': return <UserPage currentUser={currentUser} />;
      case 'currentUser-credentials': return <UserCredentialsPage currentUser={currentUser} />;
      case 'program-csr': return <ProgramCSRPage currentUser={currentUser} />;
      case 'akuntansi': return <AkuntansiPage currentUser={currentUser} />;
      case 'tarik-data': return <TarikDataPage currentUser={currentUser} />;
      case 'pengaturan': return <PengaturanPage currentUser={currentUser} />;
      case 'deposit-bank': return <DepositBankPage currentUser={currentUser} />;
      case 'request-topup-bank': return <RequestTopupBankPage currentUser={currentUser} />;
      case 'penjaga-loket': return <ShiftSettingsPage currentUser={currentUser} />;
      
      // Kasir
      case 'dashboard-kasir': return <DashboardKasir currentUser={currentUser} />;
      case 'dashboard-kasir-owner': return <DashboardKasir currentUser={currentUser} />;
      case 'verifikasi-loket': return <VerifikasiLoketPage currentUser={currentUser} />;
      case 'pengeluaran': return <PengeluaranPage currentUser={currentUser} />;
      case 'riwayat': return <RiwayatPage currentUser={currentUser} />;
      case 'tarik-data-kasir': return <TarikDataPage currentUser={currentUser} />;

      // Penjaga Loket
      case 'dashboard-loket': return <DashboardLoket currentUser={currentUser} />;
      case 'dashboard-loket-owner': return <DashboardLoket currentUser={currentUser} />;
      case 'laporan-shift': return <LaporanShiftDualBankV2 user={currentUser} api={API_URL} />;
      case 'riwayat-loket': return <RiwayatLoketPage currentUser={currentUser} api={API_URL} />;
      case 'tarik-data-loket': return <TarikDataPage currentUser={currentUser} />;
      case 'deposit-bank-info': return <DepositManagement currentUser={currentUser} api={api} />;

      // Technical Jobs
      case 'technical-jobs': return <TechnicalJobsPage currentUser={currentUser} api={API_URL} />;

      // Laporan Kasir Harian (KASIR, SUPERKASIR, MANAGER, OWNER)
      case 'laporan-kasir': return <LaporanKasirDetail currentUser={currentUser} api={API_URL} />;
      case 'riwayat-laporan-kasir': return <RiwayatLaporanKasirDetail currentUser={currentUser} api={API_URL} />;

      // IT Role
      case 'dashboard-it': return <ITDashboard setCurrentPage={setCurrentPage} />;
      case 'system-logs': return <SystemLogViewer />;
      case 'tanya-it': return <TanyaITList />;

      // Inject Saldo
      case 'inject-saldo': return <InjectSaldoManager currentUser={currentUser} api={api} />;
      case 'request-inject-saldo': return <RequestInjectSaldo currentUser={currentUser} api={api} />;

      // Default fallback based on role
      default:
        if (currentUser.role === 'PENJAGA_LOKET') return <DashboardLoket currentUser={currentUser} />;
        if (currentUser.role === 'IT') return <ITDashboard setCurrentPage={setCurrentPage} />;
        if (currentUser.role === 'TECHNICIAN') return <TechnicalJobsPage currentUser={currentUser} api={API_URL} />;
        if (currentUser.role === 'KASIR' || currentUser.role === 'SUPERKASIR') return <DashboardKasir currentUser={currentUser} />;
        return <DashboardOwner currentUser={currentUser} />;
    }
  };

  return (
    <AuthContext.Provider value={{ currentUser, handleLogout }}>
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar currentPage={currentPage} setCurrentPage={setCurrentPage} currentUser={currentUser} onLogout={handleLogout} isMobileOpen={isMobileMenuOpen} setIsMobileOpen={setIsMobileMenuOpen} showTanyaITModal={showTanyaITModal} setShowTanyaITModal={setShowTanyaITModal} />
        
        <main className="flex-1 min-w-0">
          <header className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between">
            <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 hover:bg-gray-100 rounded-lg"><Menu size={24} /></button>
            <h1 className="font-bold text-gray-900">Sip Gelis</h1>
            <div className="w-10" />
          </header>
          
          <div className="p-4 lg:p-6">{renderPage()}</div>
        </main>
      </div>

      {/* Tanya IT Modal - Global Access for All Roles */}
      {showTanyaITModal && (
        <TanyaITModal
          isOpen={showTanyaITModal}
          onClose={() => setShowTanyaITModal(false)}
          onSubmit={() => {
            // Refresh if currently on tanya-it page
            if (currentPage === 'tanya-it') {
              window.location.reload();
            }
          }}
        />
      )}
    </AuthContext.Provider>
  );
}

export default App;
// Force rebuild v1771435633
